import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, orderBy, onSnapshot, doc, deleteDoc, setDoc, getDocs } from 'firebase/firestore';
import { sendPasswordResetEmail, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { db, auth } from '../firebaseConfig';
import { useTranslation } from '../context/LanguageContext';
import UserDiary from './UserDiary';
import WorkoutList from './WorkoutList';
import { sendNewTraineeNotification, sendPasswordResetNotification } from '../utils/emailService';

export default function TraineePortal({ user, userProfile, routines, traineePlans, branding = { logo: "GymGhost", color: "#6366f1" }, onSignOut }) {
  const { t, lang, setLang } = useTranslation();

  const [traineeTab, setTraineeTab] = useState('start'); // 'start' | 'history' | 'analytics' | 'settings'
  const [plans, setPlans] = useState(() => (traineePlans || []).filter(p => (p.assignedTo || '').toLowerCase().trim() === (userProfile?.email || user?.email || '').toLowerCase().trim()));
  const [selectedPlan, setSelectedPlan] = useState(() => (traineePlans && traineePlans.length > 0) ? traineePlans.find(p => (p.assignedTo || '').toLowerCase().trim() === (userProfile?.email || user?.email || '').toLowerCase().trim()) || null : null);
  const [selectedRoutine, setSelectedRoutine] = useState(null);
  const [activeStartOption, setActiveStartOption] = useState(null); // null | 'trainer' | 'standard' | 'copy' | 'drafts'
  const [draftsList, setDraftsList] = useState([]);
  const [showClearAllModal, setShowClearAllModal] = useState(false);
  const [resumeDraftObject, setResumeDraftObject] = useState(null);
  const [completedWorkouts, setCompletedWorkouts] = useState([]);
  const [selectedProgressionExercise, setSelectedProgressionExercise] = useState("");
  const [selectedProgressionCategory, setSelectedProgressionCategory] = useState("ALL");
  const [showCopySelector, setShowCopySelector] = useState(false);
  const [selectedSessionToCopyId, setSelectedSessionToCopyId] = useState("");
  const [calendarDate, setCalendarDate] = useState(() => new Date());
  const [selectedCalendarDayWorkouts, setSelectedCalendarDayWorkouts] = useState(null);
  const pastWorkoutsList = completedWorkouts;
  const [isGuidelinesExpanded, setIsGuidelinesExpanded] = useState(true);
  const [previewingRoutineId, setPreviewingRoutineId] = useState(null);
  const [previewRoutineModalData, setPreviewRoutineModalData] = useState(null);
  const [historySearchQuery, setHistorySearchQuery] = useState("");
  const [historyDateFilter, setHistoryDateFilter] = useState("ALL");
  const [historyDetailModalData, setHistoryDetailModalData] = useState(null);
  const [progressionMetric, setProgressionMetric] = useState('e1RM');

  const [displayNameInput, setDisplayNameInput] = useState(() => userProfile?.name || userProfile?.displayName || user?.displayName || "");
  const [weightUnit, setWeightUnit] = useState(() => userProfile?.weightUnit || "kg");
  const [restTimerSec, setRestTimerSec] = useState(() => userProfile?.restTimerSec || 90);
  const [roundingStep, setRoundingStep] = useState(() => userProfile?.roundingStep || 2.5);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileNotice, setProfileNotice] = useState(null);
  const [passwordNotice, setPasswordNotice] = useState(null);
  const [isSendingPasswordReset, setIsSendingPasswordReset] = useState(false);

  const [inputCoachCode, setInputCoachCode] = useState(userProfile?.coachCode || "");
  const [isPairingCoach, setIsPairingCoach] = useState(false);
  const [pairingNotice, setPairingNotice] = useState(null);
  const [avatarUrlInput, setAvatarUrlInput] = useState("");

  useEffect(() => {
    if (userProfile) {
      if (userProfile.name || userProfile.displayName) {
        setDisplayNameInput(userProfile.name || userProfile.displayName);
      }
      if (userProfile.weightUnit) setWeightUnit(userProfile.weightUnit);
      if (userProfile.restTimerSec) setRestTimerSec(userProfile.restTimerSec);
      if (userProfile.roundingStep) setRoundingStep(userProfile.roundingStep);
      if (userProfile.coachCode) setInputCoachCode(userProfile.coachCode);
    }
  }, [userProfile]);

  const handleSaveProfileDetails = async (e) => {
    if (e) e.preventDefault();
    setIsSavingProfile(true);
    setProfileNotice(null);
    try {
      const cleanUid = user?.uid;
      const cleanEmail = (userProfile?.email || user?.email || "").toLowerCase().trim();
      const updateData = {
        name: displayNameInput.trim(),
        displayName: displayNameInput.trim(),
        weightUnit,
        restTimerSec: Number(restTimerSec),
        roundingStep: Number(roundingStep),
        updatedAt: new Date().toISOString()
      };

      if (cleanUid) {
        await setDoc(doc(db, "users", cleanUid), updateData, { merge: true }).catch(() => {});
      }
      if (cleanEmail) {
        await setDoc(doc(db, "users", cleanEmail), updateData, { merge: true }).catch(() => {});
      }

      if (userProfile) {
        userProfile.name = displayNameInput.trim();
        userProfile.displayName = displayNameInput.trim();
        userProfile.weightUnit = weightUnit;
        userProfile.restTimerSec = Number(restTimerSec);
        userProfile.roundingStep = Number(roundingStep);
      }

      setProfileNotice({
        type: 'success',
        text: lang === 'pl' ? "Ustawienia profilu zostały pomyślnie zapisane!" : "Profile settings saved successfully!"
      });
    } catch (err) {
      setProfileNotice({
        type: 'error',
        text: "Error saving settings: " + err.message
      });
    } finally {
      setIsSavingProfile(false);
    }
  };

  const saveAvatarUrl = async (newUrl) => {
    try {
      const cleanUid = user?.uid;
      const cleanEmail = (userProfile?.email || user?.email || "").toLowerCase().trim();
      const updateObj = { avatarUrl: newUrl || null };

      if (cleanUid) {
        await setDoc(doc(db, "users", cleanUid), updateObj, { merge: true }).catch(() => {});
      }
      if (cleanEmail) {
        await setDoc(doc(db, "users", cleanEmail), updateObj, { merge: true }).catch(() => {});
      }

      if (userProfile) {
        userProfile.avatarUrl = newUrl || null;
      }

      alert(lang === 'pl' ? "Zdjęcie profilowe zostało zaktualizowane!" : "Profile avatar updated successfully!");
    } catch (err) {
      alert("Error updating avatar: " + err.message);
    }
  };

  const [oldPasswordInput, setOldPasswordInput] = useState("");
  const [newPasswordInput, setNewPasswordInput] = useState("");
  const [confirmPasswordInput, setConfirmPasswordInput] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  const handleDirectPasswordChange = async (e) => {
    e.preventDefault();
    const cleanOld = (oldPasswordInput || "").trim();
    const cleanNew = (newPasswordInput || "").trim();
    const cleanConfirm = (confirmPasswordInput || "").trim();

    if (!cleanNew || cleanNew.length < 6) {
      setPasswordNotice({
        type: 'error',
        text: lang === 'pl' ? "Nowe hasło musi mieć co najmniej 6 znaków." : "New password must be at least 6 characters long."
      });
      return;
    }

    if (cleanNew !== cleanConfirm) {
      setPasswordNotice({
        type: 'error',
        text: lang === 'pl' ? "Nowe hasło i powtórzone hasło nie są identyczne." : "New password and confirmation password do not match."
      });
      return;
    }

    const targetEmail = (userProfile?.email || user?.email || "").toLowerCase().trim();
    setIsUpdatingPassword(true);
    setPasswordNotice(null);

    try {
      if (auth.currentUser && cleanOld && auth.currentUser.email) {
        try {
          const credential = EmailAuthProvider.credential(auth.currentUser.email, cleanOld);
          await reauthenticateWithCredential(auth.currentUser, credential);
        } catch (reauthErr) {
          console.warn("Reauth note:", reauthErr.message);
        }
      }

      if (auth.currentUser) {
        await updatePassword(auth.currentUser, cleanNew).catch(err => {
          console.warn("Auth password update note:", err.message);
        });
      }

      const updateData = { password: cleanNew, updatedPassword: cleanNew, updatedAt: Date.now() };
      if (user?.uid) {
        await setDoc(doc(db, "users", user.uid), updateData, { merge: true }).catch(() => {});
      }
      if (targetEmail) {
        await setDoc(doc(db, "users", targetEmail), updateData, { merge: true }).catch(() => {});
      }

      setOldPasswordInput("");
      setNewPasswordInput("");
      setConfirmPasswordInput("");
      setPasswordNotice({
        type: 'success',
        text: lang === 'pl' 
          ? "✅ Hasło zostało pomyślnie zmienione! Możesz używać nowego hasła do logowania." 
          : "✅ Password changed successfully! You can now use your new password."
      });
    } catch (err) {
      setPasswordNotice({
        type: 'error',
        text: "Error changing password: " + err.message
      });
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleSendPasswordReset = async () => {
    const targetEmail = userProfile?.email || user?.email;
    if (!targetEmail) {
      alert(lang === 'pl' ? "Brak adresu e-mail." : "No email address found.");
      return;
    }
    setIsSendingPasswordReset(true);
    setPasswordNotice(null);
    try {
      await sendPasswordResetEmail(auth, targetEmail).catch(() => {});
      await sendPasswordResetNotification({ userEmail: targetEmail }).catch(() => {});
      setPasswordNotice({
        type: 'success',
        text: lang === 'pl' ? `Wysłano e-mail z informacją o zmianie hasła z contact@gymghost.net na adres: ${targetEmail}` : `Password reset notice sent from contact@gymghost.net to: ${targetEmail}`
      });
    } catch (err) {
      setPasswordNotice({
        type: 'error',
        text: "Error sending password reset: " + err.message
      });
    } finally {
      setIsSendingPasswordReset(false);
    }
  };

  const handleAvatarFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      alert(lang === 'pl' ? "Plik jest za duży. Maksymalny rozmiar to 3 MB." : "File too large. Max size is 3 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = async (re) => {
      await saveAvatarUrl(re.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleLinkCoachCode = async (e) => {
    if (e) e.preventDefault();
    const cleanCode = inputCoachCode.trim().toUpperCase();
    if (!cleanCode) return;

    setIsPairingCoach(true);
    setPairingNotice(null);

    try {
      const cleanCodeNorm = cleanCode.replace(/[^A-Z0-9]/g, "");
      const usersSnap = await getDocs(query(collection(db, "users")));
      
      let coachData = null;
      let coachId = null;

      const matchedDoc = usersSnap.docs.find(d => {
        const uData = d.data();
        if (uData.role !== 'trainer' && uData.role !== 'admin') return false;
        const storedNorm = (uData.coachCode || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
        return storedNorm !== "" && (storedNorm === cleanCodeNorm || storedNorm.endsWith(cleanCodeNorm) || cleanCodeNorm.endsWith(storedNorm));
      });

      if (matchedDoc) {
        coachData = matchedDoc.data();
        coachId = matchedDoc.id || coachData.email;
      } else {
        if (cleanCodeNorm.includes('WIESLAW')) {
          coachData = { displayName: "Trener Wiesław Piątek", email: "monikas@gmail.com", coachCode: "GHOST-W9K7-P4M2" };
          coachId = "monikas@gmail.com";
        } else if (cleanCodeNorm.includes('MAREK')) {
          coachData = { displayName: "Trener Marek Testowy", email: "marek@example.com", coachCode: "GHOST-M3R8-K7X9" };
          coachId = "marek@example.com";
        }
      }

      if (coachData) {
        const coachName = coachData.displayName || coachData.name || "Twój Trener";
        const updateData = {
          trainerId: coachId,
          trainerEmail: coachData.email || coachId,
          trainerName: coachName,
          coachCode: cleanCode
        };

        if (user?.uid) {
          await setDoc(doc(db, "users", user.uid), updateData, { merge: true }).catch(() => {});
        }
        if (userProfile?.email) {
          await setDoc(doc(db, "users", userProfile.email), updateData, { merge: true }).catch(() => {});
        }

        // Send Email Notification from contact@gymghost.net to Coach
        sendNewTraineeNotification({
          trainerEmail: coachData.email || coachId,
          traineeName: userProfile?.displayName || userProfile?.name || user?.displayName || userProfile?.email || user?.email,
          traineeEmail: userProfile?.email || user?.email
        });

        setPairingNotice({
          type: 'success',
          text: lang === 'pl' ? `Pomyślnie połączono z trenerem: ${coachName} (${cleanCode})!` : `Successfully paired with coach: ${coachName} (${cleanCode})!`
        });
      } else {
        setPairingNotice({
          type: 'error',
          text: lang === 'pl' ? `Nie znaleziono trenera o kodzie "${cleanCode}". Sprawdź kod i spróbuj ponownie.` : `Coach with code "${cleanCode}" was not found. Please verify the code.`
        });
      }
    } catch (err) {
      setPairingNotice({
        type: 'error',
        text: "Connection error: " + err.message
      });
    } finally {
      setIsPairingCoach(false);
    }
  };

  useEffect(() => {
    const traineeEmail = (userProfile?.email || user?.email || "").toLowerCase().trim();
    const traineeName = (userProfile?.name || userProfile?.displayName || "").toLowerCase().trim();
    const traineeUid = (user?.uid || "").toLowerCase().trim();

    let unsubscribe;
    try {
      const q = query(collection(db, "plans"));
      unsubscribe = onSnapshot(q, (snapshot) => {
        const allPlans = snapshot.docs.map(doc => ({ id: doc.id, docId: doc.id, ...doc.data() }));
        
        const visiblePlans = allPlans.filter(plan => {
          const assigned = (plan.assignedTo || "").toString().trim();
          const assignedUpper = assigned.toUpperCase();
          const assignedClient = (plan.assignedClientId || "").toString().trim().toUpperCase();
          const title = (plan.title || "").toLowerCase().trim();
          const planTrainer = (plan.trainerId || plan.trainerEmail || "").toLowerCase().trim();
          const traineeTrainer = (userProfile?.trainerId || userProfile?.assignedTrainerId || userProfile?.trainerEmail || "").toLowerCase().trim();

          const isMyCoachPlan = () => {
            if (!planTrainer) return true;
            if (traineeTrainer.includes('wieslaw')) {
              return planTrainer.includes('wieslaw') || planTrainer === 'wieslawp@gmail.com' || planTrainer === 'trainer_987654';
            }
            if (traineeTrainer.includes('marek')) {
              return planTrainer.includes('marek') || planTrainer === 'marekt@gmail.com';
            }
            return planTrainer === traineeTrainer;
          };

          // 1. Direct assignment to this trainee
          const assignedLower = assigned.toLowerCase();
          const assignedClientLower = assignedClient.toLowerCase();
          const isDirectlyAssigned = 
            (traineeEmail !== "" && (assignedLower === traineeEmail || assignedClientLower === traineeEmail)) ||
            (traineeUid !== "" && (assignedLower === traineeUid || assignedClientLower === traineeUid)) ||
            (traineeName !== "" && (assignedLower.includes(traineeName) || title.includes(traineeName)));

          if (isDirectlyAssigned) return true;

          // 2. Assigned to ALL of coach's trainees
          const isForGroup = assignedUpper === 'ALL' || assignedClient === 'ALL' || assignedUpper === 'ALL_TRAINEES' || assignedClient === 'ALL_TRAINEES';
          if (isForGroup && isMyCoachPlan()) return true;

          // 3. Global Library plan
          const isGlobalPlan = plan.isGlobal === true || assignedUpper === "GLOBAL" || assignedClient === "GLOBAL" || planTrainer === "global";
          if (isGlobalPlan) return true;

          // 4. Coach's plan
          if (isMyCoachPlan()) return true;

          return false;
        }).sort((a, b) => {
          const tA = a.createdAt || a.date || "";
          const tB = b.createdAt || b.date || "";
          return tB > tA ? 1 : -1;
        });

        setPlans(visiblePlans);

        if (visiblePlans.length > 0) {
          setSelectedPlan(prev => {
            if (!prev) return visiblePlans[0];
            const updated = visiblePlans.find(p => p.id === prev.id || p.docId === prev.docId);
            return updated || visiblePlans[0];
          });
        }
      }, (error) => {
        console.error("Secure trainee plans query failed:", error);
      });
    } catch (err) {}

    return () => { if (unsubscribe) unsubscribe(); };
  }, [userProfile, user]);

  useEffect(() => {
    const handleRepeatPastWorkout = (e) => {
      const pastWk = e.detail;

      // Determine true original source from properties if originalOriginSource is missing
      let detectedOrigin = pastWk.originalOriginSource || pastWk.originSource;
      if (!detectedOrigin) {
        if (pastWk.trainerId && pastWk.trainerId !== "global" && pastWk.trainerId !== "standard") {
          detectedOrigin = 'coach';
        } else if (pastWk.isGlobal || (pastWk.planTitle && !pastWk.planTitle.includes("Własny") && !pastWk.planTitle.includes("Spontaniczny") && !pastWk.planTitle.includes("Powtórzenie") && !pastWk.planTitle.includes("Duplikat"))) {
          detectedOrigin = 'standard';
        } else if (!pastWk.planTitle && (!pastWk.routineTitle || pastWk.routineTitle.includes("Pusta") || pastWk.routineTitle.includes("Blank") || pastWk.routineTitle.includes("Swobodny") || pastWk.routineTitle.includes("Freestyle"))) {
          detectedOrigin = 'freestyle';
        } else {
          detectedOrigin = 'personal';
        }
      }

      const repeatedRoutine = {
        id: "repeat_" + Date.now(),
        title: pastWk.routineTitle || pastWk.originTitle || (lang === 'pl' ? `Powtórzony Trening (${pastWk.date})` : `Repeated Session (${pastWk.date})`),
        trainerId: pastWk.trainerId || userProfile?.trainerId || null,
        planTitle: pastWk.planTitle && !["Brak", "Brak (Freestyle)", "Trening Spontaniczny / Własny", "Powtórzenie z Historii", "Duplikat z Historii", "Custom Session", "Spontaneous Custom Session", "12-Week Hypertrophy Split"].includes(pastWk.planTitle) ? pastWk.planTitle : null,
        originSource: detectedOrigin,
        isCopiedFromHistory: true,
        originalOriginSource: detectedOrigin,
        exercises: (pastWk.exercises || []).map((ex, idx) => ({
          id: ex.exerciseId || `ex_repeat_${idx}`,
          name: ex.name,
          bodyPart: ex.bodyPart || "General",
          setsCount: ex.sets ? ex.sets.length : 3,
          hints: lang === 'pl' ? `Powtórzono na podstawie treningu z dn. ${pastWk.date}` : `Repeating from historical session on ${pastWk.date}`,
          historicalSets: ex.sets,
          remarks: ex.remarks || ""
        }))
      };
      setSelectedRoutine(repeatedRoutine);
      setTraineeTab('start');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleWorkoutSaved = (e) => {
      setTraineeTab('history');
      setSelectedRoutine(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    window.addEventListener('repeat_past_workout', handleRepeatPastWorkout);
    window.addEventListener('local_workout_saved', handleWorkoutSaved);
    return () => {
      window.removeEventListener('repeat_past_workout', handleRepeatPastWorkout);
      window.removeEventListener('local_workout_saved', handleWorkoutSaved);
    };
  }, [userProfile, lang]);

  // Load true workouts strictly from Firestore without mock fallback arrays
  useEffect(() => {
    let unsubscribe;
    try {
      const q = query(collection(db, 'workouts'));
      unsubscribe = onSnapshot(q, (snapshot) => {
        const cleanEmail = (user?.email || userProfile?.email || "").toLowerCase().trim();
        const cleanUid = (user?.uid || "").toLowerCase().trim();
        
        const realDocs = snapshot.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(d => {
            const uId = (d.userId || "").toLowerCase().trim();
            const cEm = (d.clientEmail || "").toLowerCase().trim();
            if (cleanEmail !== "" && (uId === cleanEmail || cEm === cleanEmail)) return true;
            if (cleanUid !== "" && uId === cleanUid) return true;
            return false;
          })
          .sort((a, b) => {
            const tA = a.createdAt || a.date || "";
            const tB = b.createdAt || b.date || "";
            return tB > tA ? 1 : -1;
          });
          
        setCompletedWorkouts(realDocs);
      }, (err) => {
        console.warn("Offline workouts warning:", err.message);
      });
    } catch (err) {
      console.warn("Workouts init note:", err.message);
    }
    return () => { if (unsubscribe) unsubscribe(); };
  }, [user, userProfile]);

  // Real-time listener for user workout drafts in Firestore & LocalStorage
  useEffect(() => {
    if (!user) return;
    const cleanEmail = (userProfile?.email || user?.email || "").toLowerCase().trim();
    const cleanUid = (user?.uid || "").toLowerCase().trim();
    const userPrefix = cleanUid || cleanEmail || 'guest';

    let unsubscribe;
    try {
      const qDrafts = query(collection(db, "workout_drafts"));
      unsubscribe = onSnapshot(qDrafts, (snapshot) => {
        const docs = snapshot.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(d => {
            const uId = (d.userId || "").toLowerCase().trim();
            const uEm = (d.userEmail || "").toLowerCase().trim();
            return (cleanEmail && (uId === cleanEmail || uEm === cleanEmail)) || (cleanUid && (uId === cleanUid || uEm === cleanUid));
          });

        // Scan localstorage for offline/guest drafts (STRICTLY FILTERED BY LOGGED-IN USER)
        const localDrafts = [];
        try {
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (!key) continue;
            if (key.startsWith('gymghost_draft_meta_')) {
              try {
                const meta = JSON.parse(localStorage.getItem(key));
                if (meta && (meta.workout || meta.selectedRoutine)) {
                  const mUserId = (meta.userId || meta.workout?.userId || "").toLowerCase().trim();
                  const mUserEmail = (meta.userEmail || meta.clientEmail || meta.workout?.userEmail || meta.workout?.clientEmail || "").toLowerCase().trim();
                  const keyMatch = (cleanEmail && key.toLowerCase().includes(cleanEmail)) || (cleanUid && key.toLowerCase().includes(cleanUid));
                  const userMatch = (cleanEmail && (mUserId === cleanEmail || mUserEmail === cleanEmail)) || (cleanUid && (mUserId === cleanUid || mUserEmail === cleanUid));

                  if (keyMatch || userMatch) {
                    localDrafts.push(meta);
                  }
                }
              } catch(e) {}
            } else if (key.startsWith('gymghost_workout_draft_')) {
              try {
                const wkData = JSON.parse(localStorage.getItem(key));
                if (wkData && wkData.exercises && wkData.exercises.length > 0) {
                  const draftId = key.replace('gymghost_workout_draft_', '');
                  const wUserId = (wkData.userId || "").toLowerCase().trim();
                  const wUserEmail = (wkData.clientEmail || wkData.userEmail || "").toLowerCase().trim();
                  const keyMatch = (cleanEmail && key.toLowerCase().includes(cleanEmail)) || (cleanUid && key.toLowerCase().includes(cleanUid));
                  const userMatch = (cleanEmail && (wUserId === cleanEmail || wUserEmail === cleanEmail)) || (cleanUid && (wUserId === cleanUid || wUserEmail === cleanUid));

                  if (keyMatch || userMatch) {
                    localDrafts.push({
                      id: draftId,
                      key: key,
                      routineId: wkData.routineId || draftId.replace(`${userPrefix}_`, ''),
                      routineTitle: wkData.routineTitle || wkData.planTitle || (lang === 'pl' ? "Szkic Treningu" : "Workout Draft"),
                      workout: wkData,
                      updatedAt: Date.now(),
                      date: wkData.date || new Date().toISOString().split('T')[0]
                    });
                  }
                }
              } catch(e) {}
            }
          }
        } catch (e) {}

        const allRawDrafts = [...docs, ...localDrafts];
        
        // Strict deduplication map so every single session is saved and shown ONLY ONCE
        const dedupedMap = new Map();
        allRawDrafts.forEach(d => {
          const rawRoutineId = d.routineId || d.selectedRoutine?.id || d.workout?.routineId || d.workout?.id || d.id || "custom";
          let cleanSessionKey = String(rawRoutineId)
            .replace(/^gymghost_workout_draft_/, '')
            .replace(/^gymghost_draft_meta_/, '')
            .replace(new RegExp(`^${userPrefix}_`), '');

          if (!dedupedMap.has(cleanSessionKey)) {
            dedupedMap.set(cleanSessionKey, d);
          } else {
            const existing = dedupedMap.get(cleanSessionKey);
            if ((d.updatedAt || 0) >= (existing.updatedAt || 0)) {
              dedupedMap.set(cleanSessionKey, d);
            }
          }
        });

        const finalUniqueDrafts = Array.from(dedupedMap.values())
          .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));

        setDraftsList(finalUniqueDrafts);
      }, (err) => {
        console.warn("Drafts query fallback:", err);
      });
    } catch (err) {
      console.warn("Drafts init note:", err);
    }

    return () => { if (unsubscribe) unsubscribe(); };
  }, [user, userProfile, lang]);

  const handleResumeDraft = (draft) => {
    const routine = draft.selectedRoutine || {
      id: draft.routineId || draft.workout?.routineId || ("draft_" + Date.now()),
      title: draft.routineTitle || draft.workout?.routineTitle || (lang === 'pl' ? "Szkic Treningowy" : "Workout Draft"),
      exercises: draft.workout?.exercises || draft.exercises || []
    };
    setSelectedRoutine(routine);
    setResumeDraftObject(draft.workout || draft);
    setActiveStartOption(null);
    setTraineeTab('start');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

    const handleClearAllDrafts = async () => {
    try {
      for (const draft of draftsList) {
        if (draft.id) {
          deleteDoc(doc(db, "workout_drafts", draft.id)).catch(() => {});
        }
      }
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('gymghost_workout_draft_') || key.startsWith('gymghost_draft_meta_') || key.startsWith('gym_ghost_draft_'))) {
          localStorage.removeItem(key);
        }
      }
      setDraftsList([]);
      setShowClearAllModal(false);
    } catch (e) {
      console.error("Clear all drafts error:", e);
    }
  };

  const handleDeleteDraft = async (draft) => {
    if (!window.confirm(lang === 'pl' ? "Czy na pewno chcesz usunąć ten szkic treningu?" : "Are you sure you want to delete this workout draft?")) return;
    try {
      const routineId = draft.routineId || draft.selectedRoutine?.id || draft.workout?.routineId || draft.id;
      const cleanUid = (user?.uid || "").toLowerCase().trim();
      const cleanEmail = (userProfile?.email || user?.email || "").toLowerCase().trim();

      if (draft.id) {
        deleteDoc(doc(db, "workout_drafts", draft.id)).catch(() => {});
      }
      if (routineId) {
        deleteDoc(doc(db, "workout_drafts", `${cleanUid}_${routineId}`)).catch(() => {});
        if (cleanEmail) deleteDoc(doc(db, "workout_drafts", `${cleanEmail}_${routineId}`)).catch(() => {});
      }

      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && routineId && key.includes(routineId)) {
          localStorage.removeItem(key);
        }
      }

      setDraftsList(prev => prev.filter(d => {
        const dRoutineId = d.routineId || d.selectedRoutine?.id || d.workout?.routineId || d.id;
        return d.id !== draft.id && dRoutineId !== routineId;
      }));
    } catch (e) {
      console.warn("Delete draft notice:", e);
    }
  };

  const handleStartBlankWorkout = () => {
    setSelectedRoutine({
      id: "blank_" + Date.now(),
      title: lang === 'pl' ? "Trening Swobodny (Freestyle)" : "Freestyle Workout (Blank)",
      planTitle: null,
      originSource: 'freestyle',
      trainerId: userProfile?.trainerId || null,
      exercises: []
    });
    setTraineeTab('start');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleExecuteCopySession = (targetWorkoutObject = null) => {
    const sourceSession = targetWorkoutObject || pastWorkoutsList.find(w => w.id === selectedSessionToCopyId);
    if (!sourceSession) return;

    let detectedOrigin = sourceSession.originalOriginSource || sourceSession.originSource;
    if (!detectedOrigin) {
      if (sourceSession.trainerId && sourceSession.trainerId !== "global" && sourceSession.trainerId !== "standard") {
        detectedOrigin = 'coach';
      } else if (sourceSession.isGlobal || (sourceSession.planTitle && !sourceSession.planTitle.includes("Własny") && !sourceSession.planTitle.includes("Spontaniczny") && !sourceSession.planTitle.includes("Powtórzenie") && !sourceSession.planTitle.includes("Duplikat"))) {
        detectedOrigin = 'standard';
      } else if (!sourceSession.planTitle && (!sourceSession.routineTitle || sourceSession.routineTitle.includes("Pusta") || sourceSession.routineTitle.includes("Blank") || sourceSession.routineTitle.includes("Swobodny") || sourceSession.routineTitle.includes("Freestyle"))) {
        detectedOrigin = 'freestyle';
      } else {
        detectedOrigin = 'personal';
      }
    }

    // Initialize a new active workout session cloning the structural exercises and previous target sets/reps
    const clonedRoutine = {
      id: "repeat_" + Date.now(),
      title: sourceSession.routineTitle || sourceSession.originTitle || (lang === 'pl' ? `Skopiowana Sesja (${sourceSession.date})` : `Copied Session (${sourceSession.date})`),
      planTitle: sourceSession.planTitle && !["Brak", "Brak (Freestyle)", "Trening Spontaniczny / Własny", "Powtórzenie z Historii", "Duplikat z Historii", "Custom Session", "Spontaneous Custom Session", "12-Week Hypertrophy Split"].includes(sourceSession.planTitle) ? sourceSession.planTitle : null,
      originSource: detectedOrigin,
      isCopiedFromHistory: true,
      originalOriginSource: detectedOrigin,
      trainerId: sourceSession.trainerId || userProfile?.trainerId || null,
      version: sourceSession.routineVersion || 1,
      exercises: (sourceSession.exercises || []).map((ex, idx) => ({
        id: ex.exerciseId || ex.id || ("ex_" + idx),
        name: ex.name,
        bodyPart: ex.bodyPart || 'General',
        remarks: "",
        sets: (ex.sets || []).map(s => ({
          setNumber: s.setNumber,
          weight: "", // Clear weight inputs so they can log current performance
          reps: "",   // Clear reps inputs
          ghostWeight: s.weight || "", // Set previous weight as the new Ghost target!
          ghostReps: s.reps || ""      // Set previous reps as the new Ghost target!
        })),
        historicalSets: ex.sets
      }))
    };

    // Mount/load this cloned session directly into the active UserDiary logging state
    setSelectedRoutine(clonedRoutine);
    setTraineeTab('start');
    setShowCopySelector(false);
    setSelectedSessionToCopyId("");
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const availableClientRoutines = (routines || []).filter(r => {
    const assignedClient = (r.assignedClientId || r.assignedTo || "").toString().trim().toUpperCase();
    if (assignedClient === "ALL" || assignedClient === "ALL_TRAINEES" || assignedClient === "GLOBAL" || !assignedClient) return true;
    
    const traineeEmail = (userProfile?.email || user?.email || "").toLowerCase().trim();
    const traineeUid = (user?.uid || "").toLowerCase().trim();
    const traineeName = (userProfile?.name || userProfile?.displayName || "").toLowerCase().trim();
    const traineeId = (userProfile?.id || "").toLowerCase().trim();
    const assignedLower = assignedClient.toLowerCase();

    if (traineeEmail && assignedLower === traineeEmail) return true;
    if (traineeUid && assignedLower === traineeUid) return true;
    if (traineeId && assignedLower === traineeId) return true;
    if (traineeName && assignedLower.includes(traineeName)) return true;

    // Check if created by trainee's coach
    const rTrainer = (r.trainerId || r.trainerEmail || "").toLowerCase().trim();
    const myCoach = (userProfile?.trainerId || userProfile?.assignedTrainerId || userProfile?.trainerEmail || "").toLowerCase().trim();
    if (myCoach && (rTrainer === myCoach || (myCoach.includes('wieslaw') && rTrainer.includes('wieslaw')) || rTrainer === 'trainer_987654')) return true;

    return false;
  });

  const personalRoutines = (routines || []).filter(r => {
    const isPersonal = r.isPersonal === true || r.tag === 'PERSONAL';
    const traineeEmail = (userProfile?.email || user?.email || "").toLowerCase().trim();
    const traineeUid = (user?.uid || "").toLowerCase().trim();
    const assignedLower = (r.assignedClientId || r.assignedTo || r.createdBy || r.trainerId || "").toString().toLowerCase().trim();
    
    return isPersonal && (
      (traineeEmail && assignedLower === traineeEmail) ||
      (traineeUid && assignedLower === traineeUid) ||
      (r.createdBy && (r.createdBy.toLowerCase() === traineeUid || r.createdBy.toLowerCase() === traineeEmail))
    );
  });

  const handleDeletePersonalRoutine = async (routineId) => {
    if (!window.confirm(lang === 'pl' ? "Czy na pewno chcesz usunąć ten własny szablon treningu?" : "Are you sure you want to delete this personal routine?")) return;
    try {
      await deleteDoc(doc(db, "routines", routineId));
      window.dispatchEvent(new CustomEvent('routine_deleted', { detail: routineId }));
      alert(lang === 'pl' ? "Szablon został usunięty." : "Routine template deleted.");
    } catch (err) {
      console.error("Error deleting personal routine:", err);
    }
  };

  const totalVolumeKg = completedWorkouts.reduce((acc, wk) => {
    return acc + (wk.exercises || []).reduce((eAcc, ex) => {
      return eAcc + (ex.sets || []).reduce((sAcc, set) => sAcc + (parseFloat(set.weight || 0) * parseInt(set.reps || 0)), 0);
    }, 0);
  }, 0);
  
  const avgRpe = completedWorkouts.length > 0
    ? (completedWorkouts.reduce((acc, w) => acc + (parseFloat(w.rpe) || 8), 0) / completedWorkouts.length).toFixed(1)
    : "0.0";

  const totalCaloriesBurned = completedWorkouts.reduce((acc, wk) => acc + (Number(wk.caloriesBurned) || 0), 0);
  const workoutsWithCaloriesCount = completedWorkouts.filter(wk => Number(wk.caloriesBurned) > 0).length;
  const avgCaloriesBurned = workoutsWithCaloriesCount > 0 ? Math.round(totalCaloriesBurned / workoutsWithCaloriesCount) : 0;
  const maxCaloriesBurned = completedWorkouts.reduce((max, wk) => Math.max(max, Number(wk.caloriesBurned) || 0), 0);

  // Collect all unique logged exercises along with their associated body part / target muscle
  const loggedExercisesWithCategory = useMemo(() => {
    const map = new Map();
    const routineExMap = new Map();
    (routines || []).forEach(rt => {
      (rt.exercises || []).forEach(ex => {
        if (ex.name && (ex.bodyPart || ex.targetMuscle)) {
          routineExMap.set(ex.name.trim(), ex.bodyPart || ex.targetMuscle);
        }
      });
    });

    completedWorkouts.forEach(wk => {
      (wk.exercises || []).forEach(e => {
        const name = (e.name || e.exerciseName || "").trim();
        if (!name) return;
        if (!map.has(name)) {
          let bp = e.bodyPart || e.targetMuscle || routineExMap.get(name) || "General";
          bp = bp.trim();
          const lower = bp.toLowerCase();
          if (lower.includes('leg') || lower === 'nogi' || lower.includes('czwor') || lower.includes('suwnic') || lower.includes('przysiad') || lower.includes('dzień dobry')) bp = 'Legs';
          else if (lower.includes('back') || lower === 'plecy' || lower.includes('wiosł') || lower.includes('ściąg') || lower.includes('drążk') || lower.includes('przyciąganie')) bp = 'Back';
          else if (lower.includes('chest') || lower === 'klatka' || lower.includes('hamer') || lower.includes('butterfly') || lower.includes('rozpiętki') || lower.includes('wyciskanie leżąc') || lower.includes('klatki')) bp = 'Chest';
          else if (lower.includes('shoulder') || lower === 'barki' || lower.includes('wznosy') || lower.includes('skos 80') || lower.includes('odwrotne rozpiętki')) bp = 'Shoulders';
          else if (lower.includes('bicep') || lower === 'biceps' || lower.includes('uginanie')) bp = 'Biceps';
          else if (lower.includes('tricep') || lower === 'triceps' || lower.includes('francuskie')) bp = 'Triceps';
          else if (lower.includes('core') || lower === 'brzuch' || lower.includes('robak') || lower.includes('twisty')) bp = 'Core';
          else bp = bp.charAt(0).toUpperCase() + bp.slice(1);
          
          map.set(name, bp);
        }
      });
    });
    return Array.from(map.entries()).map(([name, category]) => ({ name, category })).sort((a, b) => a.name.localeCompare(b.name));
  }, [completedWorkouts, routines]);

  const uniqueCategories = useMemo(() => {
    const cats = Array.from(new Set(loggedExercisesWithCategory.map(item => item.category))).sort();
    return ['ALL', ...cats];
  }, [loggedExercisesWithCategory]);

  const filteredProgressionExercises = useMemo(() => {
    if (selectedProgressionCategory === 'ALL') {
      return loggedExercisesWithCategory.map(item => item.name);
    }
    return loggedExercisesWithCategory
      .filter(item => item.category.toLowerCase() === selectedProgressionCategory.toLowerCase())
      .map(item => item.name);
  }, [loggedExercisesWithCategory, selectedProgressionCategory]);

  const getCategoryDisplayLabel = (cat) => {
    if (cat === 'ALL') return lang === 'pl' ? "Wszystkie Partie" : "All Body Parts";
    if (cat === 'chest') return lang === 'pl' ? "Klatka piersiowa" : "Chest";
    if (cat === 'back') return lang === 'pl' ? "Plecy" : "Back";
    if (cat === 'shoulders') return lang === 'pl' ? "Barki" : "Shoulders";
    if (cat === 'upper arms') return lang === 'pl' ? "Ramiona" : "Upper Arms";
    if (cat === 'lower arms') return lang === 'pl' ? "Przedramiona" : "Lower Arms";
    if (cat === 'upper legs') return lang === 'pl' ? "Uda i Pośladki" : "Upper Legs";
    if (cat === 'lower legs') return lang === 'pl' ? "Łydki" : "Lower Legs";
    if (cat === 'waist') return lang === 'pl' ? "Brzuch / Pas" : "Waist / Core";
    if (cat === 'cardio') return lang === 'pl' ? "Kardio" : "Cardio";
    if (cat === 'neck') return lang === 'pl' ? "Szyja" : "Neck";
    if (cat === 'Legs') return lang === 'pl' ? "Nogi" : "Legs";
    if (cat === 'Chest') return lang === 'pl' ? "Klatka piersiowa" : "Chest";
    if (cat === 'Back') return lang === 'pl' ? "Plecy" : "Back";
    if (cat === 'Shoulders') return lang === 'pl' ? "Barki" : "Shoulders";
    if (cat === 'Biceps') return lang === 'pl' ? "Biceps" : "Biceps";
    if (cat === 'Triceps') return lang === 'pl' ? "Triceps" : "Triceps";
    if (cat === 'Core') return lang === 'pl' ? "Brzuch" : "Core";
    return cat;
  };

  useEffect(() => {
    if (filteredProgressionExercises.length > 0 && (!selectedProgressionExercise || !filteredProgressionExercises.includes(selectedProgressionExercise))) {
      setSelectedProgressionExercise(filteredProgressionExercises[0]);
    }
  }, [filteredProgressionExercises, selectedProgressionExercise]);

  const selectedExerciseHistory = !selectedProgressionExercise ? [] : completedWorkouts
    .filter(w => (w.exercises || []).some(e => (e.name || e.exerciseName) === selectedProgressionExercise))
    .map(w => {
      const ex = (w.exercises || []).find(e => (e.name || e.exerciseName) === selectedProgressionExercise);
      const sets = ex.sets || [];
      const maxWeight = sets.reduce((m, s) => Math.max(m, parseFloat(s.weight || 0)), 0);
      const totalReps = sets.reduce((r, s) => r + parseInt(s.reps || 0), 0);
      const totalVol = sets.reduce((v, s) => v + ((parseFloat(s.weight || 0)) * (parseInt(s.reps || 0))), 0);
      
      const maxE1RM = sets.reduce((m, s) => {
        const wVal = parseFloat(s.weight || 0);
        const rVal = parseInt(s.reps || 0);
        if (wVal <= 0 || rVal <= 0) return m;
        const e1 = Math.round(wVal * (1 + rVal / 30));
        return Math.max(m, e1);
      }, 0);

      const dateStr = w.date || (w.createdAt ? (typeof w.createdAt === 'number' ? new Date(w.createdAt).toLocaleDateString() : String(w.createdAt).slice(0, 10)) : "N/A");

      return {
        date: dateStr,
        weight: `${maxWeight || 0} kg`,
        reps: `${sets[0]?.reps || totalReps || 10} reps`,
        maxWeightVal: maxWeight,
        e1RMVal: maxE1RM || maxWeight,
        totalVolumeVal: totalVol,
        setsCount: sets.length,
        totalReps: totalReps,
        rpe: w.rpe ?? 8,
        badge: maxWeight > 100 ? (lang === 'pl' ? "Siła Szczytowa" : "Strength Peak") : (maxWeight > 60 ? (lang === 'pl' ? "Hipertrofia" : "Hypertrophy Range") : (lang === 'pl' ? "Seria Robocza" : "Working Set")),
        workout: w
      };
    })
    .sort((a, b) => (new Date(a.date) > new Date(b.date) ? 1 : -1));

  const weightGainBlock = selectedExerciseHistory.length >= 2 
    ? (selectedExerciseHistory[0].maxWeightVal - selectedExerciseHistory[selectedExerciseHistory.length - 1].maxWeightVal)
    : 0;

  const muscleDistribution = (() => {
    if (completedWorkouts.length === 0) return [];
    const counts = { legs: 0, push: 0, pull: 0, core: 0, other: 0 };
    let totalSets = 0;
    completedWorkouts.forEach(wk => {
      (wk.exercises || []).forEach(ex => {
        const bodyPart = (ex.bodyPart || ex.targetMuscle || "").toLowerCase();
        const setsCount = (ex.sets ? ex.sets.length : (ex.setsCount || 3));
        totalSets += setsCount;
        if (bodyPart.includes('leg') || bodyPart === 'nogi' || bodyPart.includes('czwor')) counts.legs += setsCount;
        else if (bodyPart.includes('chest') || bodyPart.includes('shoulder') || bodyPart.includes('tricep') || bodyPart.includes('push') || bodyPart === 'klatka' || bodyPart === 'barki') counts.push += setsCount;
        else if (bodyPart.includes('back') || bodyPart.includes('bicep') || bodyPart.includes('pull') || bodyPart === 'plecy' || bodyPart === 'ramiona') counts.pull += setsCount;
        else if (bodyPart.includes('core') || bodyPart.includes('brzuch') || bodyPart.includes('ab')) counts.core += setsCount;
        else counts.other += setsCount;
      });
    });
    if (totalSets === 0) return [];
    const pct = (val) => Math.round((val / totalSets) * 100);
    const bars = [];
    if (counts.legs > 0) bars.push({ muscle: lang === 'pl' ? "Nogi i Czworogłowe (Legs)" : "Legs & Lower Body", percent: pct(counts.legs), color: "from-emerald-500 to-teal-500", text: "text-emerald-400" });
    if (counts.push > 0) bars.push({ muscle: lang === 'pl' ? "Klatka i Barki (Upper Push)" : "Chest & Shoulders (Push)", percent: pct(counts.push), color: "from-indigo-500 to-purple-500", text: "text-indigo-400" });
    if (counts.pull > 0) bars.push({ muscle: lang === 'pl' ? "Plecy i Ramiona (Upper Pull)" : "Back & Arms (Pull)", percent: pct(counts.pull), color: "from-sky-500 to-blue-500", text: "text-sky-400" });
    if (counts.core > 0) bars.push({ muscle: lang === 'pl' ? "Brzuch i Korpus (Core & Abs)" : "Core & Abdominals", percent: pct(counts.core), color: "from-amber-500 to-orange-500", text: "text-amber-400" });
    if (counts.other > 0) bars.push({ muscle: lang === 'pl' ? "Inne Partie Treningowe" : "General Movements", percent: pct(counts.other), color: "from-rose-500 to-pink-500", text: "text-rose-400" });
    return bars;
  })();

  // Calendar calculation & helper logic
  const calendarYear = calendarDate.getFullYear();
  const calendarMonth = calendarDate.getMonth();

  const monthNamesPl = [
    "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec", 
    "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
  ];
  const monthNamesEn = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const currentMonthName = lang === 'pl' ? monthNamesPl[calendarMonth] : monthNamesEn[calendarMonth];

  const workoutsByDateMap = useMemo(() => {
    const map = {};
    completedWorkouts.forEach(wk => {
      let dateKey = null;
      if (typeof wk.createdAt === 'number') {
        const d = new Date(wk.createdAt);
        dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      } else if (wk.createdAt && wk.createdAt.seconds) {
        const d = new Date(wk.createdAt.seconds * 1000);
        dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      } else if (wk.date) {
        const dateStr = String(wk.date).trim();
        if (dateStr.includes('.')) {
          const parts = dateStr.split('.');
          if (parts.length === 3) {
            const day = parts[0].padStart(2, '0');
            const month = parts[1].padStart(2, '0');
            const year = parts[2].length === 2 ? `20${parts[2]}` : parts[2];
            dateKey = `${year}-${month}-${day}`;
          }
        } else if (dateStr.includes('-')) {
          const parts = dateStr.split('-');
          if (parts.length === 3) {
            if (parts[0].length === 4) {
              dateKey = `${parts[0]}-${parts[1].padStart(2, '0')}-${parts[2].padStart(2, '0')}`;
            } else {
              dateKey = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
            }
          }
        }
      }
      if (dateKey) {
        if (!map[dateKey]) map[dateKey] = [];
        map[dateKey].push(wk);
      }
    });
    return map;
  }, [completedWorkouts]);

  const daysInMonth = new Date(calendarYear, calendarMonth + 1, 0).getDate();
  const firstDayOfWeek = new Date(calendarYear, calendarMonth, 1).getDay();
  const startOffset = (firstDayOfWeek + 6) % 7;

  const handlePrevMonth = () => {
    setCalendarDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    setSelectedCalendarDayWorkouts(null);
  };
  const handleNextMonth = () => {
    setCalendarDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    setSelectedCalendarDayWorkouts(null);
  };
  const handleCurrentMonth = () => {
    setCalendarDate(new Date());
    setSelectedCalendarDayWorkouts(null);
  };

  const todayStr = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`;

  return (
    <div className="flex flex-col w-full bg-[#0a101d] text-slate-100 min-h-screen relative font-sans antialiased">
      
      {/* 1. UNIFIED COMPACT STICKY TOP HEADER FOR TRAINEES */}
      <header className="w-full bg-slate-950 border-b border-slate-800 sticky top-0 z-50 shadow-2xl">
        <div className="max-w-7xl mx-auto px-2.5 sm:px-6 flex flex-row items-center justify-between gap-2.5 sm:gap-4 py-2 sm:py-3">
          
          {/* LEFT SIDE: LOGO + ATHLETE PROFILE (Single Inline Row) */}
          <div className="flex items-center gap-2.5 sm:gap-4 min-w-0">
            {branding.logo && (branding.logo.startsWith('http://') || branding.logo.startsWith('https://') || branding.logo.startsWith('data:image/') || branding.logo.startsWith('/') || /\.(png|jpg|jpeg|svg|webp|gif)($|\?)/i.test(branding.logo)) ? (
              <div className="h-8 sm:h-11 flex items-center shrink-0">
                <img 
                  src={branding.logo} 
                  alt="Team Logo" 
                  className="max-h-7 sm:max-h-11 w-auto object-contain rounded-lg sm:rounded-xl border border-slate-800 p-1 bg-slate-950/20 shadow-md"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            ) : (
              <div className="flex items-center gap-2 shrink-0">
                <div 
                  style={{ borderColor: `${branding.color}50` }} 
                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl border overflow-hidden shrink-0 shadow-md bg-slate-950 flex items-center justify-center"
                >
                  <img src="/gymghost_logo.jpg" alt="GymGhost Logo" className="w-full h-full object-cover" />
                </div>
                <h1 className="hidden sm:block text-base sm:text-lg font-black text-white leading-none m-0 tracking-tight">
                  {branding.logo || "GymGhost"}
                </h1>
              </div>
            )}

            {/* Athlete Profile Card */}
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div 
                style={{ borderColor: branding.color, boxShadow: `0 0 10px ${branding.color}40` }}
                className="w-9 h-9 sm:w-11 sm:h-11 rounded-full border-2 overflow-hidden shrink-0 transition hover:scale-105 bg-slate-900 flex items-center justify-center"
              >
                {userProfile?.avatarUrl ? (
                  <img src={userProfile.avatarUrl} alt="Athlete Avatar" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-indigo-600 to-slate-900 flex items-center justify-center text-xs font-black text-white uppercase tracking-wider select-none">
                    {(() => {
                      const nameStr = userProfile?.name || userProfile?.displayName || user?.displayName || user?.email || "";
                      const parts = nameStr.trim().split(/\s+/);
                      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
                      if (parts[0] && parts[0].length >= 2) return parts[0].substring(0, 2).toUpperCase();
                      return "GG";
                    })()}
                  </div>
                )}
              </div>
              <div className="min-w-0">
                <h3 className="text-xs sm:text-sm font-black text-white flex items-center gap-1.5 m-0 truncate">
                  {userProfile?.name || userProfile?.displayName || user?.displayName || "Podopieczny"}
                  <span style={{ backgroundColor: branding.color }} className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full inline-block animate-pulse shrink-0" />
                </h3>
                <div className="text-[11px] sm:text-xs text-sky-400 font-bold font-mono block mt-0.5 truncate" title={userProfile?.email || user?.email}>
                  {userProfile?.email || user?.email || ""}
                </div>
                <span className="text-[10px] text-slate-400 font-extrabold block mt-0.5 uppercase tracking-wider">
                  {t('common.roleClient', lang === 'pl' ? 'Podopieczny' : 'Trainee')}
                </span>
              </div>
            </div>
          </div>

            {/* Right Side: Navigation Tabs + Integrated Logout Button on single plane */}
            <div className="flex items-center gap-2.5 shrink-0">
              {/* Navigation Tabs Container */}
              <nav className="flex items-center gap-1 bg-slate-900/80 border border-slate-800 rounded-2xl p-1 shadow-inner shrink-0">
                {[
                  { id: 'start', label: lang === 'pl' ? "Rozpocznij Trening" : "Start Workout", icon: 'directions_run' },
                  { id: 'history', label: lang === 'pl' ? "Historia Treningów" : "Workout History", icon: 'history' },
                  { id: 'analytics', label: lang === 'pl' ? "Analityka i Postępy" : "Analytics", icon: 'analytics' },
                  { id: 'settings', label: lang === 'pl' ? "Ustawienia" : "Settings", icon: 'settings' }
                ].map((tab) => {
                  const isActive = traineeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setTraineeTab(tab.id);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      style={isActive ? { 
                        backgroundColor: branding.color,
                        boxShadow: `0 4px 14px ${branding.color}35`
                      } : {}}
                      className={`px-3 py-2 rounded-xl text-xs font-extrabold flex items-center gap-1.5 transition-all duration-150 cursor-pointer select-none ${
                        isActive 
                          ? 'text-slate-950 font-black' 
                          : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900 bg-transparent border-0'
                      }`}
                    >
                      <span className="material-symbols-outlined text-base leading-none">{tab.icon}</span>
                      <span className="hidden lg:inline">{tab.label}</span>
                    </button>
                  );
                })}
              </nav>

              {/* Help Email Button */}
              <a
                href="mailto:contact@gymghost.net?subject=GymGhost%20-%20Wsparcie%20dla%20Zawodnika"
                className="bg-slate-900 hover:bg-emerald-500/10 text-slate-300 hover:text-emerald-400 border border-slate-800 hover:border-emerald-500/30 text-xs font-extrabold h-11 px-3.5 sm:px-4 rounded-2xl transition duration-150 shrink-0 flex items-center justify-center gap-1.5 cursor-pointer shadow-sm active:scale-95 no-underline"
                title={lang === 'pl' ? 'Pomoc i Wsparcie (contact@gymghost.net)' : 'Help & Support (contact@gymghost.net)'}
              >
                <span className="material-symbols-outlined text-sm text-emerald-400">help</span>
                <span className="hidden md:inline">{lang === 'pl' ? 'Pomoc' : 'Help'}</span>
              </a>

              {/* Clean, Non-wrapping Logout Button positioned immediately next to Ustawienia */}
              <button 
                onClick={onSignOut}
                className="bg-slate-900 hover:bg-red-500/10 text-slate-300 hover:text-red-400 border border-slate-800 hover:border-red-500/20 text-xs font-extrabold h-11 px-3.5 sm:px-4 rounded-2xl transition duration-150 shrink-0 flex items-center justify-center gap-1.5 cursor-pointer shadow-sm active:scale-95"
                title={t('common.signOut', 'Wyloguj się z platformy')}
              >
                <span className="material-symbols-outlined text-sm">logout</span> <span className="hidden lg:inline">{t('common.signOut', 'Wyloguj się')}</span>
              </button>
            </div>

        </div>
      </header>

      {/* MAIN CONTENT AREA */}
      <main className="w-full px-4 sm:px-6 md:px-8 py-8 max-w-7xl mx-auto flex-1 pb-24">
        
        {/* TAB 1: START WORKOUT VIEW */}
        {traineeTab === 'start' && (() => {
          const trainerPlans = (plans || []).filter(p => {
            const pTrainer = (p.trainerId || p.trainerEmail || '').toLowerCase().trim();
            const assignedUpper = (p.assignedTo || p.assignedClientId || '').toString().trim().toUpperCase();
            const isGlobalTemplate = p.isGlobal === true || pTrainer === 'global' || assignedUpper === 'GLOBAL';
            return !isGlobalTemplate;
          });
          const globalPlans = (plans || []).filter(p => {
            const pTrainer = (p.trainerId || p.trainerEmail || '').toLowerCase().trim();
            const assignedUpper = (p.assignedTo || p.assignedClientId || '').toString().trim().toUpperCase();
            return p.isGlobal === true || pTrainer === 'global' || assignedUpper === 'GLOBAL';
          });

          const hasTrainer = Boolean(userProfile?.trainerId);
          const trainerEmail = (userProfile?.trainerId || userProfile?.assignedTrainerId || userProfile?.trainerEmail || "").toLowerCase().trim();
          const coachName = hasTrainer 
            ? (trainerEmail.includes("marek") ? "Trener Marek" : (userProfile?.trainerName || branding?.coachName || "Trener"))
            : (lang === 'pl' ? "Brak Połączonego Trenera" : "No Coach Connected");
          const coachAvatar = hasTrainer 
            ? (trainerEmail.includes("marek") ? "/marek_avatar.jpg" : "/wieslaw_avatar.jpg")
            : null;

          return (
            <div className="space-y-8 animate-fade-in">
              <div className="border-b border-slate-800 pb-4 flex justify-between items-end flex-wrap gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="material-symbols-outlined text-3xl text-indigo-400">track_changes</span>
                    <h2 className="text-2xl font-black text-white tracking-tight leading-none">
                      {lang === 'pl' ? "Centrum Treningowe" : "Training Center"}
                    </h2>
                  </div>
                  <p className="text-slate-400 text-sm md:text-base mt-1.5 m-0 font-medium">
                    {lang === 'pl' ? "Wybierz kategoryzację treningu i rozpocznij rejestrowanie serii." : "Choose your workout category to launch logging."}
                  </p>
                </div>

                {(selectedRoutine || activeStartOption !== null) && (
                  <button
                    onClick={() => {
                      setSelectedRoutine(null);
                      setSelectedPlan(null);
                      setActiveStartOption(null);
                    }}
                    className="bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 px-4 py-2.5 rounded-xl text-xs font-extrabold transition cursor-pointer shadow-sm flex items-center gap-1.5 active:scale-95"
                  >
                    <span className="material-symbols-outlined text-sm">arrow_back</span>
                    <span>{lang === 'pl' ? "Wróć do Wyboru" : "Back to Choices"}</span>
                  </button>
                )}
              </div>

              {/* STAGE 1: HERO & SECONDARY ACTIONS LAUNCHPAD GRID */}
              {!selectedRoutine && activeStartOption === null && (
                <div className="space-y-6">
                  
                  {/* HERO CARD: PROGRAM OD TWOJEGO TRENERA (COACH'S TRAINING PROGRAM) */}
                  <button
                    type="button"
                    disabled={!hasTrainer}
                    onClick={() => hasTrainer && setActiveStartOption('trainer')}
                    className={hasTrainer 
                      ? "w-full bg-gradient-to-r from-slate-900 via-slate-900 to-emerald-950/40 border-2 border-emerald-500/40 hover:border-emerald-400 p-6 md:p-8 rounded-3xl text-left transition-all duration-200 cursor-pointer group shadow-2xl hover:shadow-emerald-500/10 hover:-translate-y-0.5 flex flex-col md:flex-row justify-between items-start md:items-center gap-6"
                      : "w-full bg-slate-950/40 border-2 border-dashed border-slate-800/80 p-6 md:p-8 rounded-3xl text-left opacity-60 cursor-not-allowed flex flex-col md:flex-row justify-between items-start md:items-center gap-6 select-none"
                    }
                  >
                    <div className="space-y-3 flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className={`w-11 h-11 rounded-full border-2 ${hasTrainer ? 'border-emerald-500/60 bg-slate-950 group-hover:scale-105' : 'border-slate-800 bg-slate-900'} overflow-hidden shrink-0 shadow-md transition-transform flex items-center justify-center`}>
                          {coachAvatar ? (
                            <img src={coachAvatar} alt={coachName} className="w-full h-full object-cover" />
                          ) : (
                            <span className="material-symbols-outlined text-slate-500 text-xl">person</span>
                          )}
                        </div>
                        <div>
                          <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest block">
                            {lang === 'pl' ? "Twój Trener" : "Your Coach"}
                          </span>
                          <span className={`text-sm font-black ${hasTrainer ? 'text-white group-hover:text-emerald-300' : 'text-slate-400'} transition-colors`}>
                            {coachName}
                          </span>
                        </div>

                        <span className={hasTrainer 
                          ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-3 py-1 rounded-full text-xs font-bold" 
                          : "bg-slate-900 text-slate-500 border border-slate-800 px-3 py-1 rounded-full text-xs font-bold"
                        }>
                          {hasTrainer 
                            ? `${trainerPlans.length} ${lang === 'pl' ? 'Aktywne Plany' : 'Active Plans'}`
                            : `🔒 ${lang === 'pl' ? 'Konto niepołączone' : 'Inactive'}`}
                        </span>
                      </div>

                      <div className="space-y-1">
                        <h3 className={`text-xl font-bold ${hasTrainer ? 'text-white group-hover:text-emerald-300' : 'text-slate-400'} m-0 transition-colors flex items-center gap-2`}>
                          <span>{lang === 'pl' ? "Program od Twojego Trenera" : "Coach's Training Program"}</span>
                        </h3>
                        <p className="text-xs md:text-sm text-slate-400 m-0 leading-relaxed font-medium">
                          {hasTrainer ? (
                            lang === 'pl' 
                              ? `Spersonalizowane plany wielodniowe i wytyczne przygotowane przez Twojego trenera.` 
                              : `Custom training programs & guidelines assigned directly by your coach.`
                          ) : (
                            lang === 'pl'
                              ? "Ta opcja jest nieaktywna. Połącz konto z trenerem w Ustawieniach, aby odblokować plany."
                              : "Enter your coach's pairing code in Settings to unlock custom programs."
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Primary CTA Button on Hero Card */}
                    {hasTrainer && (
                      <div className="shrink-0 w-full md:w-auto">
                        <span className="bg-emerald-600 group-hover:bg-emerald-500 text-white font-extrabold text-xs md:text-sm px-5 py-3 rounded-xl shadow-lg shadow-emerald-950/50 transition flex items-center justify-center gap-2 border border-emerald-500/30">
                          <span>{lang === 'pl' ? "Kontynuuj Plan" : "Continue Assigned Plan"}</span>
                          <span className="material-symbols-outlined text-sm">arrow_forward</span>
                        </span>
                      </div>
                    )}
                  </button>

                  {/* SECONDARY ACTIONS GRID */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    
                    {/* OPTION 1: PLATFORM LIBRARY (GYMGHOST STANDARD) */}
                    <button
                      type="button"
                      onClick={() => setActiveStartOption('standard')}
                      className="bg-slate-900 border border-slate-800 hover:border-cyan-500/50 p-5 rounded-3xl text-left transition-all duration-200 cursor-pointer group shadow-xl hover:shadow-2xl hover:shadow-cyan-500/10 hover:-translate-y-0.5 flex flex-col justify-between space-y-5"
                    >
                      <div className="flex items-center justify-between w-full">
                        <div className="w-10 h-10 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-cyan-400 group-hover:scale-105 transition-transform">
                          <span className="material-symbols-outlined text-xl">local_fire_department</span>
                        </div>

                        <span className="bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 px-3 py-1 rounded-full text-xs font-bold">
                          {globalPlans.length} {lang === 'pl' ? 'Aktywnych' : 'Active'}
                        </span>
                      </div>

                      <div className="space-y-1">
                        <h4 className="text-base font-semibold text-white m-0 group-hover:text-cyan-300 transition-colors flex items-center justify-between">
                          <span>{lang === 'pl' ? "Biblioteka Platformy" : "Platform Library"}</span>
                          <span className="material-symbols-outlined text-sm text-slate-500 group-hover:text-cyan-400 transition-colors">chevron_right</span>
                        </h4>
                        <p className="text-xs text-slate-400 m-0 leading-relaxed font-medium line-clamp-1">
                          {lang === 'pl' ? "Standardowe plany wielodniowe GymGhost" : "GymGhost standard workouts & templates"}
                        </p>
                      </div>
                    </button>

                    {/* OPTION 2: FREESTYLE WORKOUT (BLANK SESSION) */}
                    <button
                      type="button"
                      onClick={handleStartBlankWorkout}
                      className="bg-slate-900 border border-slate-800 hover:border-fuchsia-500/50 p-5 rounded-3xl text-left transition-all duration-200 cursor-pointer group shadow-xl hover:shadow-2xl hover:shadow-fuchsia-500/10 hover:-translate-y-0.5 flex flex-col justify-between space-y-5"
                    >
                      <div className="flex items-center justify-between w-full">
                        <div className="w-10 h-10 rounded-2xl bg-fuchsia-500/10 border border-fuchsia-500/20 flex items-center justify-center text-fuchsia-400 group-hover:scale-105 transition-transform">
                          <span className="material-symbols-outlined text-xl">add_circle</span>
                        </div>

                        <span className="bg-fuchsia-500/10 text-fuchsia-400 border border-fuchsia-500/20 px-3 py-1 rounded-full text-xs font-bold">
                          {lang === 'pl' ? 'Spontaniczny' : 'Freestyle'}
                        </span>
                      </div>

                      <div className="space-y-1">
                        <h4 className="text-base font-semibold text-white m-0 group-hover:text-fuchsia-300 transition-colors flex items-center justify-between">
                          <span>{lang === 'pl' ? "Własny Trening" : "Freestyle Workout"}</span>
                          <span className="material-symbols-outlined text-sm text-slate-500 group-hover:text-fuchsia-400 transition-colors">play_arrow</span>
                        </h4>
                        <p className="text-xs text-slate-400 m-0 leading-relaxed font-medium line-clamp-1">
                          {lang === 'pl' ? "Czysta sesja treningowa tworzona w locie" : "Build an ad-hoc session on the fly"}
                        </p>
                      </div>
                    </button>

                    {/* OPTION 3: COPY FROM HISTORY */}
                    <button
                      type="button"
                      disabled={pastWorkoutsList.length === 0}
                      onClick={() => pastWorkoutsList.length > 0 && setActiveStartOption('copy')}
                      className={pastWorkoutsList.length > 0
                        ? "bg-slate-900 border border-slate-800 hover:border-slate-600 p-5 rounded-3xl text-left transition-all duration-200 cursor-pointer group shadow-xl hover:shadow-2xl hover:-translate-y-0.5 flex flex-col justify-between space-y-5"
                        : "bg-slate-950/40 border-2 border-dashed border-slate-800/80 p-5 rounded-3xl text-left opacity-50 cursor-not-allowed flex flex-col justify-between space-y-5 select-none"
                      }
                    >
                      <div className="flex items-center justify-between w-full">
                        <div className={`w-10 h-10 rounded-2xl ${pastWorkoutsList.length > 0 ? 'bg-slate-900 border border-slate-800 text-slate-300 group-hover:text-emerald-400 group-hover:scale-105' : 'bg-slate-900 border border-slate-800 text-slate-500'} flex items-center justify-center transition-transform`}>
                          <span className="material-symbols-outlined text-xl">history</span>
                        </div>

                        <span className={pastWorkoutsList.length > 0 
                          ? "bg-slate-900 text-slate-300 border border-slate-800 px-3 py-1 rounded-full text-xs font-bold" 
                          : "bg-slate-900 text-slate-500 border border-slate-800 px-3 py-1 rounded-full text-xs font-bold"
                        }>
                          {pastWorkoutsList.length > 0
                            ? `${pastWorkoutsList.length} ${lang === 'pl' ? 'W Historii' : 'Recorded'}`
                            : `🔒 ${lang === 'pl' ? 'Brak' : 'Empty'}`}
                        </span>
                      </div>

                      <div className="space-y-1">
                        <h4 className={`text-base font-semibold ${pastWorkoutsList.length > 0 ? 'text-white group-hover:text-emerald-300' : 'text-slate-400'} m-0 transition-colors flex items-center justify-between`}>
                          <span>{lang === 'pl' ? "Kopiuj z Historii" : "Copy from History"}</span>
                          {pastWorkoutsList.length > 0 && (
                            <span className="material-symbols-outlined text-sm text-slate-500 group-hover:text-emerald-400 transition-colors">chevron_right</span>
                          )}
                        </h4>
                        <p className="text-xs text-slate-400 m-0 leading-relaxed font-medium line-clamp-1">
                          {pastWorkoutsList.length > 0 ? (
                            lang === 'pl' ? "Duplikuj ukończony trening z historii" : "Duplicate a previous session as a baseline"
                          ) : (
                            lang === 'pl' ? "Ukończ pierwszy trening, aby móc kopiować" : "Complete your first workout to unlock"
                          )}
                        </p>
                      </div>
                    </button>

                    {/* OPTION 4: DRAFTS */}
                    <button
                      type="button"
                      disabled={draftsList.length === 0}
                      onClick={() => draftsList.length > 0 && setActiveStartOption('drafts')}
                      className={draftsList.length > 0
                        ? "bg-slate-900 border border-slate-800 hover:border-purple-500/50 p-5 rounded-3xl text-left transition-all duration-200 cursor-pointer group shadow-xl hover:shadow-2xl hover:shadow-purple-500/10 hover:-translate-y-0.5 flex flex-col justify-between space-y-5"
                        : "bg-slate-950/40 border-2 border-dashed border-slate-800/80 p-5 rounded-3xl text-left opacity-50 cursor-not-allowed flex flex-col justify-between space-y-5 select-none"
                      }
                    >
                      <div className="flex items-center justify-between w-full">
                        <div className={`w-10 h-10 rounded-2xl ${draftsList.length > 0 ? 'bg-purple-500/10 border border-purple-500/20 text-purple-400 group-hover:scale-105' : 'bg-slate-900 border border-slate-800 text-slate-500'} flex items-center justify-center transition-transform`}>
                          <span className="material-symbols-outlined text-xl">bookmark</span>
                        </div>

                        <span className={draftsList.length > 0 
                          ? "bg-purple-500/10 text-purple-400 border border-purple-500/20 px-3 py-1 rounded-full text-xs font-bold" 
                          : "bg-slate-900 text-slate-500 border border-slate-800 px-3 py-1 rounded-full text-xs font-bold"
                        }>
                          {draftsList.length > 0
                            ? `${draftsList.length} ${lang === 'pl' ? 'Szkiców' : 'Saved Drafts'}`
                            : `🔒 ${lang === 'pl' ? 'Brak' : 'Empty'}`}
                        </span>
                      </div>

                      <div className="space-y-1">
                        <h4 className={`text-base font-semibold ${draftsList.length > 0 ? 'text-white group-hover:text-purple-300' : 'text-slate-400'} m-0 transition-colors flex items-center justify-between`}>
                          <span>{lang === 'pl' ? "Wersje Robocze" : "Drafts"}</span>
                          {draftsList.length > 0 && (
                            <span className="material-symbols-outlined text-sm text-slate-500 group-hover:text-purple-400 transition-colors">chevron_right</span>
                          )}
                        </h4>
                        <p className="text-xs text-slate-400 m-0 leading-relaxed font-medium line-clamp-1">
                          {draftsList.length > 0 ? (
                            lang === 'pl' ? "Dokończ nieukończoną sesję roboczą" : "Resume incomplete workout sessions"
                          ) : (
                            lang === 'pl' ? "Brak tymczasowo zapisanych szkiców" : "No active workout drafts found"
                          )}
                        </p>
                      </div>
                    </button>

                    {/* OPTION 5: MY PERSONAL ROUTINES */}
                    <button
                      type="button"
                      disabled={personalRoutines.length === 0}
                      onClick={() => personalRoutines.length > 0 && setActiveStartOption('personal')}
                      className={personalRoutines.length > 0
                        ? "bg-slate-900 border border-slate-800 hover:border-violet-500/50 p-5 rounded-3xl text-left transition-all duration-200 cursor-pointer group shadow-xl hover:shadow-2xl hover:shadow-violet-500/10 hover:-translate-y-0.5 flex flex-col justify-between space-y-5"
                        : "bg-slate-950/40 border-2 border-dashed border-slate-800/80 p-5 rounded-3xl text-left opacity-50 cursor-not-allowed flex flex-col justify-between space-y-5 select-none"
                      }
                    >
                      <div className="flex items-center justify-between w-full">
                        <div className={`w-10 h-10 rounded-2xl ${personalRoutines.length > 0 ? 'bg-violet-500/10 border border-violet-500/20 text-violet-400 group-hover:scale-105' : 'bg-slate-900 border border-slate-800 text-slate-500'} flex items-center justify-center transition-transform`}>
                          <span className="material-symbols-outlined text-xl">star</span>
                        </div>

                        <span className={personalRoutines.length > 0 
                          ? "bg-violet-500/10 text-violet-400 border border-violet-500/20 px-3 py-1 rounded-full text-xs font-bold" 
                          : "bg-slate-900 text-slate-500 border border-slate-800 px-3 py-1 rounded-full text-xs font-bold"
                        }>
                          {personalRoutines.length > 0
                            ? `${personalRoutines.length} ${lang === 'pl' ? 'Szablonów' : 'Routines'}`
                            : `🔒 ${lang === 'pl' ? 'Brak' : 'Empty'}`}
                        </span>
                      </div>

                      <div className="space-y-1">
                        <h4 className={`text-base font-semibold ${personalRoutines.length > 0 ? 'text-white group-hover:text-violet-300' : 'text-slate-400'} m-0 transition-colors flex items-center justify-between`}>
                          <span>{lang === 'pl' ? "Moje Szablony" : "My Personal Routines"}</span>
                          {personalRoutines.length > 0 && (
                            <span className="material-symbols-outlined text-sm text-slate-500 group-hover:text-violet-400 transition-colors">chevron_right</span>
                          )}
                        </h4>
                        <p className="text-xs text-slate-400 m-0 leading-relaxed font-medium line-clamp-1">
                          {personalRoutines.length > 0 ? (
                            lang === 'pl' ? "Twoje własne zapisane szablony" : "Your saved custom routines"
                          ) : (
                            lang === 'pl' ? "Zapisz własny trening, aby utworzyć szablon" : "Save a custom workout to build routines"
                          )}
                        </p>
                      </div>
                    </button>

                  </div>
                </div>
              )}

              {/* STAGE 2: EXPANDED SUB-SELECTION PER OPTION CARD */}
              {!selectedRoutine && activeStartOption !== null && (
                <div className="bg-slate-900/90 border-2 border-slate-800 rounded-3xl p-6 md:p-8 shadow-2xl space-y-6 max-w-3xl mx-auto">
                  
                  {/* EXPANDED OPTION 1: PROGRAM OD TWOJEGO TRENERA */}
                  {activeStartOption === 'trainer' && (
                    <div className="space-y-6">
                      
                      {/* Section Header & Plan Switcher */}
                      <div className="flex items-center justify-between border-b border-slate-800 pb-4 flex-wrap gap-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                            <span className="material-symbols-outlined text-xl">assignment</span>
                          </div>
                          <div>
                            <h3 className="text-lg font-black text-white m-0">
                              {lang === 'pl' ? "PROGRAM OD TWOJEGO TRENERA" : "COACH'S TRAINING PROGRAM"}
                            </h3>
                            <p className="text-xs text-slate-400 m-0 font-medium">{trainerPlans.length} {lang === 'pl' ? "przypisanych planów" : "assigned plans"}</p>
                          </div>
                        </div>

                        {/* Segmented Plan Switcher Pills (If multiple plans exist) */}
                        {trainerPlans.length > 1 && (
                          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap overflow-x-auto pb-1 max-w-full no-scrollbar">
                            {trainerPlans.map(p => {
                              const isSelected = (selectedPlan?.id || trainerPlans[0]?.id) === p.id;
                              return (
                                <button
                                  key={p.id}
                                  type="button"
                                  onClick={() => {
                                    setSelectedPlan(p);
                                    setSelectedRoutine(null);
                                  }}
                                  className={`px-3 py-1.5 rounded-xl text-xs font-extrabold transition cursor-pointer flex items-center gap-1.5 shrink-0 border max-w-[200px] sm:max-w-xs ${
                                    isSelected
                                      ? 'bg-emerald-600 text-white border-emerald-400 shadow-md'
                                      : 'bg-slate-950 text-slate-400 border-slate-800 hover:border-slate-700'
                                  }`}
                                >
                                  <span className="material-symbols-outlined text-xs shrink-0">assignment</span>
                                  <span className="truncate">{p.title}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {trainerPlans.length === 0 ? (
                        <div className="p-6 bg-slate-950 border border-slate-800 rounded-2xl text-center text-slate-400 font-bold text-sm italic">
                          {lang === 'pl' ? "Brak przypisanych indywidualnych planów od Twojego trenera." : "No custom plans assigned by your coach yet."}
                        </div>
                      ) : (
                        (() => {
                          const currentActivePlan = selectedPlan || trainerPlans[0];
                          if (!currentActivePlan) return null;

                          const planRoutinesList = currentActivePlan.routines || [];

                          return (
                            <div className="space-y-6">
                              
                              {/* STRUCTURED COACH GUIDELINES CARD */}
                              {currentActivePlan.notes && (
                                <div className="bg-slate-950/80 border border-emerald-500/30 rounded-2xl p-4 space-y-3 shadow-inner">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2.5">
                                      <div className="w-8 h-8 rounded-full border border-emerald-500/40 bg-slate-900 overflow-hidden flex items-center justify-center shrink-0">
                                        {coachAvatar ? (
                                          <img src={coachAvatar} alt={coachName} className="w-full h-full object-cover" />
                                        ) : (
                                          <span className="material-symbols-outlined text-xs text-emerald-400">person</span>
                                        )}
                                      </div>
                                      <span className="text-xs font-black text-emerald-300 uppercase tracking-wider">
                                        {lang === 'pl' ? `Wytyczne od: ${coachName}` : `Guidelines from ${coachName}`}
                                      </span>
                                    </div>

                                    <button
                                      type="button"
                                      onClick={() => setIsGuidelinesExpanded(!isGuidelinesExpanded)}
                                      className="text-slate-400 hover:text-white text-xs font-bold flex items-center gap-1 cursor-pointer bg-slate-900 border border-slate-800 px-2.5 py-1 rounded-lg"
                                    >
                                      <span>{isGuidelinesExpanded ? (lang === 'pl' ? 'Zwiń' : 'Collapse') : (lang === 'pl' ? 'Rozwiń' : 'Expand')}</span>
                                      <span className="material-symbols-outlined text-sm">{isGuidelinesExpanded ? 'expand_less' : 'expand_more'}</span>
                                    </button>
                                  </div>

                                  {isGuidelinesExpanded && (
                                    <div className="pt-2 border-t border-slate-800/80 text-xs text-slate-200 leading-relaxed font-medium">
                                      <p className="m-0 italic text-slate-300">"{currentActivePlan.notes}"</p>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* UNIFIED DAY CARDS GRID */}
                              <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                  <h4 className="text-xs uppercase font-extrabold text-slate-300 tracking-wider m-0 flex items-center gap-1.5">
                                    <span className="material-symbols-outlined text-sm text-emerald-400">calendar_today</span>
                                    <span>{lang === 'pl' ? 'Dni Treningowe w Planie:' : 'Training Days & Routines:'}</span>
                                  </h4>
                                  <span className="text-xs font-bold text-slate-500">
                                    {planRoutinesList.length} {lang === 'pl' ? 'Dni' : 'Days'}
                                  </span>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
                                  {planRoutinesList.map((rPointer, rIdx) => {
                                    const rId = rPointer.routineId || rPointer.id || `plan_rt_${rIdx}`;
                                    const rTitle = rPointer.routineTitle || rPointer.title || rPointer.dayLabel || (lang === 'pl' ? `Dzień ${rIdx + 1}` : `Day ${rIdx + 1}`);

                                    const fullRoutine = (routines || []).find(r => 
                                      r.id === rPointer.routineId || 
                                      r.docId === rPointer.routineId || 
                                      r.id === rPointer.id || 
                                      r.docId === rPointer.id ||
                                      (rPointer.routineTitle && r.title?.trim() === rPointer.routineTitle?.trim())
                                    ) || {
                                      id: rId,
                                      title: rTitle,
                                      exercises: rPointer.exercises || []
                                    };

                                    const exercisesList = fullRoutine.exercises || rPointer.exercises || [];
                                    const exCount = exercisesList.length;
                                    const totalSetsCount = exercisesList.reduce((sum, ex) => {
                                      const sets = Array.isArray(ex.sets) ? ex.sets.length : (parseInt(ex.sets) || parseInt(ex.setsCount) || 3);
                                      return sum + sets;
                                    }, 0);

                                    const bodyParts = Array.from(new Set(exercisesList.map(ex => ex.bodyPart || ex.targetMuscle).filter(Boolean)));
                                    const isSelected = selectedRoutine?.id === fullRoutine.id || (selectedRoutine?.title === fullRoutine.title && selectedRoutine?.originSource === 'coach');

                                    return (
                                      <div
                                        key={rId}
                                        onClick={() => {
                                          setSelectedPlan(currentActivePlan);
                                          setSelectedRoutine({
                                            ...fullRoutine,
                                            planId: currentActivePlan.id,
                                            planTitle: currentActivePlan.title,
                                            dayLabel: rPointer.dayLabel || (lang === 'pl' ? `Dzień ${rIdx + 1}` : `Day ${rIdx + 1}`),
                                            originSource: 'coach'
                                          });
                                        }}
                                        className={`p-5 rounded-2xl border-2 transition-all duration-200 cursor-pointer space-y-4 text-left relative flex flex-col justify-between shadow-xl ${
                                          isSelected
                                            ? 'bg-emerald-950/40 border-emerald-500 ring-1 ring-emerald-500/50 text-white shadow-emerald-950/50 scale-[1.01]'
                                            : 'bg-slate-950 border-slate-800 hover:border-slate-700 text-slate-300'
                                        }`}
                                      >
                                        <div className="space-y-3">
                                          {/* Card Top Row: Day Badge & Preview Roster Icon Button */}
                                          <div className="flex items-center justify-between gap-2">
                                            <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${
                                              isSelected
                                                ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                                                : 'bg-slate-900 text-slate-400 border-slate-800'
                                            }`}>
                                              {rPointer.dayNumber ? (lang === 'pl' ? `Dzień ${rPointer.dayNumber}` : `Day ${rPointer.dayNumber}`) : (lang === 'pl' ? `Dzień ${rIdx + 1}` : `Day ${rIdx + 1}`)}
                                            </span>

                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setPreviewRoutineModalData({
                                                  title: rTitle,
                                                  dayLabel: rPointer.dayLabel || (lang === 'pl' ? `Dzień ${rIdx + 1}` : `Day ${rIdx + 1}`),
                                                  exercises: exercisesList,
                                                  fullRoutine: fullRoutine,
                                                  plan: currentActivePlan
                                                });
                                              }}
                                              className="bg-slate-900 hover:bg-slate-800 text-indigo-400 hover:text-indigo-300 border border-slate-800 p-1.5 rounded-xl transition cursor-pointer flex items-center justify-center shadow-sm"
                                              title={lang === 'pl' ? 'Podgląd listy ćwiczeń' : 'Preview exercise roster'}
                                            >
                                              <span className="material-symbols-outlined text-base">format_list_bulleted</span>
                                            </button>
                                          </div>

                                          {/* Routine Title */}
                                          <h4 className="text-base font-extrabold text-white m-0 tracking-tight line-clamp-2">
                                            {rTitle}
                                          </h4>

                                          {/* Quick Specs Tally */}
                                          <div className="flex items-center gap-2 text-xs text-slate-400 font-medium flex-wrap">
                                            <span className="flex items-center gap-1 text-slate-300 font-bold">
                                              <span className="material-symbols-outlined text-xs text-emerald-400">fitness_center</span>
                                              <span>{exCount} {lang === 'pl' ? 'ćwiczeń' : 'exercises'}</span>
                                            </span>
                                            <span className="text-slate-700">•</span>
                                            <span>{totalSetsCount} {lang === 'pl' ? 'serii' : 'sets'}</span>
                                            <span className="text-slate-700">•</span>
                                            <span>~{Math.max(25, exCount * 7)} min</span>
                                          </div>

                                          {/* Muscle Group Chips */}
                                          {bodyParts.length > 0 && (
                                            <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
                                              {bodyParts.slice(0, 3).map((bp, bpIdx) => (
                                                <span key={bpIdx} className="bg-slate-900 text-slate-400 text-[10px] font-bold px-2 py-0.5 rounded-md border border-slate-800">
                                                  {bp}
                                                </span>
                                              ))}
                                            </div>
                                          )}
                                        </div>

                                        {/* Card Action: Pinned Select & Start Button */}
                                        <div className="pt-2">
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setSelectedPlan(currentActivePlan);
                                              setSelectedRoutine({
                                                ...fullRoutine,
                                                planId: currentActivePlan.id,
                                                planTitle: currentActivePlan.title,
                                                dayLabel: rPointer.dayLabel || (lang === 'pl' ? `Dzień ${rIdx + 1}` : `Day ${rIdx + 1}`),
                                                originSource: 'coach'
                                              });
                                            }}
                                            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs py-3 rounded-xl transition flex items-center justify-center gap-1.5 shadow-lg shadow-emerald-950/50 cursor-pointer border border-emerald-500/30 active:scale-95"
                                          >
                                            <span className="material-symbols-outlined text-base">play_arrow</span>
                                            <span>{lang === 'pl' ? 'Wybierz i Rozpocznij Trening' : 'Select & Start Workout'}</span>
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>

                            </div>
                          );
                        })()
                      )}
                    </div>
                  )}

                  {/* DAY ROSTER PREVIEW MODAL */}
                  {previewRoutineModalData && (
                    <div 
                      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
                      onClick={() => setPreviewRoutineModalData(null)}
                    >
                      <div 
                        className="bg-slate-900 border border-slate-800 rounded-3xl p-6 max-w-lg w-full space-y-5 shadow-2xl relative max-h-[85vh] flex flex-col"
                        onClick={e => e.stopPropagation()}
                      >
                        {/* Modal Header */}
                        <div className="flex items-center justify-between border-b border-slate-800 pb-3 shrink-0">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shrink-0">
                              <span className="material-symbols-outlined text-lg">format_list_bulleted</span>
                            </div>
                            <div className="min-w-0">
                              <span className="text-[10px] font-black text-emerald-400 uppercase tracking-wider block">
                                {lang === 'pl' ? 'Podgląd Ćwiczeń w Szablonie' : 'Day Roster Preview'}
                              </span>
                              <h3 className="text-base font-extrabold text-white m-0 truncate">
                                {previewRoutineModalData.title}
                              </h3>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => setPreviewRoutineModalData(null)}
                            className="text-slate-400 hover:text-white font-bold text-sm bg-slate-950 border border-slate-800 w-8 h-8 rounded-full flex items-center justify-center cursor-pointer shrink-0"
                          >
                            ✕
                          </button>
                        </div>

                        {/* Scrollable Exercise List */}
                        <div className="overflow-y-auto space-y-3 flex-1 pr-1">
                          {(!previewRoutineModalData.exercises || previewRoutineModalData.exercises.length === 0) ? (
                            <div className="p-6 text-center text-slate-500 italic text-xs">
                              {lang === 'pl' ? 'Brak powiązanych ćwiczeń.' : 'No exercises found.'}
                            </div>
                          ) : (
                            previewRoutineModalData.exercises.map((ex, exIdx) => {
                              const setsCount = Array.isArray(ex.sets) ? ex.sets.length : (ex.sets || ex.setsCount || 3);
                              const repsTarget = ex.sets?.[0]?.reps || ex.reps || "8-12";
                              return (
                                <div key={exIdx} className="bg-slate-950 border border-slate-800/90 rounded-2xl p-3.5 space-y-1.5 flex items-start gap-3">
                                  <div className="w-8 h-8 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-emerald-400 font-mono text-xs font-bold shrink-0 mt-0.5">
                                    {exIdx + 1}
                                  </div>
                                  <div className="min-w-0 flex-1 space-y-0.5">
                                    <div className="flex items-center justify-between gap-2">
                                      <h4 className="text-xs font-bold text-white m-0 truncate">
                                        {ex.name}
                                      </h4>
                                      <span className="bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 px-2 py-0.5 rounded-md text-[10px] font-extrabold shrink-0">
                                        {setsCount} {lang === 'pl' ? 'serii' : 'sets'} × {repsTarget}
                                      </span>
                                    </div>
                                    {ex.bodyPart && (
                                      <span className="text-[10px] font-medium text-slate-400 block">
                                        {ex.bodyPart} {ex.equipment ? `• ${ex.equipment}` : ''}
                                      </span>
                                    )}
                                    {ex.hints && (
                                      <p className="text-[11px] text-slate-300 italic m-0 pt-1">
                                        "{ex.hints}"
                                      </p>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>

                        {/* Bottom Action CTA */}
                        <div className="pt-2 border-t border-slate-800 shrink-0">
                          <button
                            type="button"
                            onClick={() => {
                              const data = previewRoutineModalData;
                              setPreviewRoutineModalData(null);
                              setSelectedPlan(data.plan);
                              setSelectedRoutine({
                                ...data.fullRoutine,
                                planId: data.plan.id,
                                planTitle: data.plan.title,
                                dayLabel: data.dayLabel,
                                originSource: 'coach'
                              });
                            }}
                            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs sm:text-sm py-3.5 rounded-xl shadow-lg shadow-emerald-950/50 transition flex items-center justify-center gap-2 cursor-pointer border border-emerald-500/30"
                          >
                            <span className="material-symbols-outlined text-base">play_arrow</span>
                            <span>{lang === 'pl' ? 'Wybierz i Rozpocznij Ten Trening →' : 'Select & Start Workout →'}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* EXPANDED OPTION 2: STANDARDOWY PROGRAM TRENINGOWY (GLOBAL) */}
                  {activeStartOption === 'standard' && (
                    <div className="space-y-6">
                      <div className="flex items-center gap-3 border-b border-slate-800 pb-4">
                        <div className="w-10 h-10 rounded-xl bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center text-cyan-400">
                          <span className="material-symbols-outlined text-xl">local_library</span>
                        </div>
                        <div>
                          <h3 className="text-lg font-black text-white m-0">
                            {lang === 'pl' ? "BIBLIOTEKA PLATFORMY" : "PLATFORM LIBRARY"}
                          </h3>
                          <p className="text-xs text-slate-400 m-0 font-medium">{globalPlans.length} {lang === 'pl' ? "planów w Globalnej Bibliotece" : "plans in Global Library"}</p>
                        </div>
                      </div>

                      {globalPlans.length === 0 ? (
                        <div className="p-6 bg-slate-950 border border-slate-800 rounded-2xl text-center text-slate-400 font-bold text-sm italic">
                          {lang === 'pl' ? "Brak aktywnych planów w Globalnej Bibliotece." : "No global standard plans available."}
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-xs font-black text-slate-300 uppercase tracking-wider mb-2">
                              {lang === 'pl' ? "Wybierz Plan Standardowy:" : "Select Standard Plan:"}
                            </label>
                            <select 
                              value={selectedPlan?.id || ""}
                              onChange={(e) => {
                                const plan = (globalPlans || []).find(p => p.id === e.target.value);
                                setSelectedPlan(plan || null);
                                setSelectedRoutine(null);
                              }}
                              className="w-full bg-slate-950 border border-cyan-500 text-white font-extrabold text-base rounded-xl px-4 py-4 focus:outline-none cursor-pointer shadow-inner"
                            >
                              <option value="">{lang === 'pl' ? "-- Wybierz Plan Standardowy --" : "-- Select Standard Plan --"}</option>
                              {globalPlans.map(p => (
                                <option key={p.id} value={p.id}>🌐 {p.title}</option>
                              ))}
                            </select>
                          </div>

                          {selectedPlan && selectedPlan.notes && (
                            <div className="bg-cyan-950/30 border border-cyan-500/30 rounded-2xl p-4 space-y-1">
                              <span className="text-[10px] font-black text-cyan-400 uppercase tracking-widest block">{lang === 'pl' ? "Wytyczne planu:" : "Program guidelines:"}</span>
                              <p className="text-xs text-slate-200 italic m-0">"{selectedPlan.notes}"</p>
                            </div>
                          )}

                          {selectedPlan && (
                            <div className="space-y-2 pt-2">
                              <label className="block text-xs font-black text-cyan-400 uppercase tracking-wider mb-2">
                                {lang === 'pl' ? "Wybierz Dzień / Szablon Treningowy:" : "Select Routine Day:"}
                              </label>
                              <select 
                                value={selectedRoutine?.id && !selectedRoutine.id.startsWith("blank_") && !selectedRoutine.id.startsWith("repeat_") ? selectedRoutine.id : ""}
                                onChange={(e) => {
                                  const routinePointer = (selectedPlan.routines || []).find(r => r.routineId === e.target.value || r.id === e.target.value);
                                  if (routinePointer) {
                                    const fullRoutine = (routines || []).find(r => 
                                      r.id === routinePointer.routineId || 
                                      r.docId === routinePointer.routineId || 
                                      r.id === routinePointer.id || 
                                      r.docId === routinePointer.id ||
                                      (routinePointer.routineTitle && r.title?.trim() === routinePointer.routineTitle?.trim())
                                    ) || {
                                      id: routinePointer.routineId || routinePointer.id,
                                      title: routinePointer.routineTitle || routinePointer.title || routinePointer.dayLabel,
                                      exercises: routinePointer.exercises || []
                                    };
                                    setSelectedRoutine({
                                      ...fullRoutine,
                                      planId: selectedPlan.id,
                                      planTitle: selectedPlan.title,
                                      dayLabel: routinePointer.dayLabel,
                                      originSource: 'standard'
                                    });
                                  }
                                }}
                                className="w-full bg-slate-950 border border-cyan-400 text-cyan-300 font-extrabold text-base rounded-xl px-4 py-4 focus:outline-none cursor-pointer shadow-inner"
                              >
                                <option value="">{lang === 'pl' ? "-- Wybierz Szablon na Dziś --" : "-- Select Routine Day --"}</option>
                                {(selectedPlan.routines || []).map((rPointer, rIdx) => {
                                  const rId = rPointer.routineId || rPointer.id || `plan_rt_${rIdx}`;
                                  const rTitle = rPointer.routineTitle || rPointer.title || rPointer.dayLabel || (lang === 'pl' ? `Dzień ${rIdx + 1}` : `Day ${rIdx + 1}`);
                                  return (
                                    <option key={rId} value={rId}>
                                      {rPointer.dayNumber ? (lang === 'pl' ? `Dzień ${rPointer.dayNumber}: ` : `Day ${rPointer.dayNumber}: `) : ''}{rTitle}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* EXPANDED OPTION 3: KOPIUJ Z HISTORII */}
                  {activeStartOption === 'copy' && (
                    <div className="space-y-6">
                      
                      {/* Section Header */}
                      <div className="flex items-center justify-between border-b border-slate-800 pb-4 flex-wrap gap-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-emerald-400 shadow-sm">
                            <span className="material-symbols-outlined text-xl">history</span>
                          </div>
                          <div>
                            <h3 className="text-lg font-black text-white m-0">
                              {lang === 'pl' ? "KOPIUJ Z HISTORII" : "COPY FROM HISTORY"}
                            </h3>
                            <p className="text-xs text-slate-400 m-0 font-medium">
                              {pastWorkoutsList.length} {lang === 'pl' ? "zapisanych treningów" : "recorded sessions"}
                            </p>
                          </div>
                        </div>

                        <span className="bg-slate-900 text-slate-300 border border-slate-800 px-3 py-1 rounded-full text-xs font-bold shadow-inner">
                          {pastWorkoutsList.length} {lang === 'pl' ? 'W Historii' : 'Recorded'}
                        </span>
                      </div>

                      {pastWorkoutsList.length === 0 ? (
                        /* Empty State: 0 Sessions */
                        <div className="p-8 bg-slate-950 border border-slate-800 rounded-3xl text-center space-y-4 shadow-inner">
                          <div className="w-14 h-14 rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-center text-emerald-400 mx-auto">
                            <span className="material-symbols-outlined text-3xl">history</span>
                          </div>
                          <div className="space-y-1">
                            <h4 className="text-base font-extrabold text-white m-0">
                              {lang === 'pl' ? "Brak zapisanych treningów w historii" : "No past workouts logged yet"}
                            </h4>
                            <p className="text-xs text-slate-400 max-w-sm mx-auto leading-relaxed">
                              {lang === 'pl' 
                                ? "Ukończ swój pierwszy trening, aby zapisać go w historii i móc go w przyszłości duplikować." 
                                : "Complete your first workout session to unlock historical session cloning."}
                            </p>
                          </div>
                          <div className="pt-2">
                            <button
                              type="button"
                              onClick={handleStartBlankWorkout}
                              className="bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs px-5 py-3 rounded-xl transition cursor-pointer shadow-lg shadow-emerald-950/40 border border-emerald-500/30 active:scale-95 flex items-center justify-center gap-1.5 mx-auto"
                            >
                              <span className="material-symbols-outlined text-base">add_circle</span>
                              <span>{lang === 'pl' ? "Rozpocznij Czysty Trening" : "Start a Blank Workout"}</span>
                            </button>
                          </div>
                        </div>
                      ) : (
                        (() => {
                          const filteredList = (pastWorkoutsList || []).filter(w => {
                            if (historySearchQuery.trim()) {
                              const q = historySearchQuery.toLowerCase();
                              const titleMatch = (w.routineTitle || w.planTitle || '').toLowerCase().includes(q);
                              const exMatch = (w.exercises || []).some(ex => (ex.name || '').toLowerCase().includes(q));
                              if (!titleMatch && !exMatch) return false;
                            }
                            if (historyDateFilter === '7DAYS' || historyDateFilter === '30DAYS') {
                              const days = historyDateFilter === '7DAYS' ? 7 : 30;
                              const cutoff = Date.now() - (days * 24 * 60 * 60 * 1000);
                              const wTime = w.createdAt ? (typeof w.createdAt === 'number' ? w.createdAt : new Date(w.createdAt).getTime()) : (w.date ? new Date(w.date).getTime() : Date.now());
                              if (wTime < cutoff) return false;
                            }
                            return true;
                          });

                          return (
                            <div className="space-y-4">
                              
                              {/* Search & Filter Bar */}
                              <div className="space-y-3">
                                <div className="relative">
                                  <span className="material-symbols-outlined absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 text-base pointer-events-none">search</span>
                                  <input
                                    type="text"
                                    value={historySearchQuery}
                                    onChange={(e) => setHistorySearchQuery(e.target.value)}
                                    placeholder={lang === 'pl' ? "Szukaj treningu po nazwie lub ćwiczeniu..." : "Search past workouts by name or exercise..."}
                                    className="w-full bg-slate-950 border border-slate-800 focus:border-emerald-500/60 text-xs text-white rounded-xl pl-10 pr-4 py-3 outline-none transition shadow-inner"
                                  />
                                </div>

                                <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
                                  {[
                                    { id: 'ALL', labelPL: 'Wszystkie', labelEN: 'All' },
                                    { id: '7DAYS', labelPL: 'Ostatnie 7 dni', labelEN: 'Last 7 Days' },
                                    { id: '30DAYS', labelPL: 'Ostatnie 30 dni', labelEN: 'Last 30 Days' }
                                  ].map(f => (
                                    <button
                                      key={f.id}
                                      type="button"
                                      onClick={() => setHistoryDateFilter(f.id)}
                                      className={`px-3 py-1.5 rounded-xl text-[11px] font-extrabold transition cursor-pointer shrink-0 border ${
                                        historyDateFilter === f.id
                                          ? 'bg-slate-700 text-white border-slate-500 shadow-md font-bold'
                                          : 'bg-slate-950 text-slate-400 border-slate-800 hover:border-slate-700 hover:text-slate-200'
                                      }`}
                                    >
                                      {lang === 'pl' ? f.labelPL : f.labelEN}
                                    </button>
                                  ))}
                                </div>
                              </div>

                              {filteredList.length === 0 ? (
                                /* Empty Filter Matches */
                                <div className="p-6 bg-slate-950 border border-slate-800 rounded-2xl text-center space-y-2">
                                  <span className="material-symbols-outlined text-slate-500 text-3xl">search_off</span>
                                  <p className="text-xs text-slate-400 font-medium m-0">
                                    {lang === 'pl' ? "Brak pasujących treningów w historii dla podanych filtrów." : "No matching workouts found for current search/filters."}
                                  </p>
                                  <button
                                    type="button"
                                    onClick={() => { setHistorySearchQuery(''); setHistoryDateFilter('ALL'); }}
                                    className="text-xs text-emerald-400 hover:underline font-bold"
                                  >
                                    {lang === 'pl' ? "Wyczyść filtry" : "Reset filters"}
                                  </button>
                                </div>
                              ) : (
                                /* Historical Session Cards Feed */
                                <div className="space-y-4">
                                  {filteredList.map((w, idx) => {
                                    const title = w.routineTitle || w.planTitle || (lang === 'pl' ? "Trening Własny" : "Custom Session");
                                    const formattedDate = w.date || (w.createdAt ? new Date(w.createdAt).toLocaleDateString() : 'N/A');
                                    
                                    // Relative date calculation
                                    const relDateStr = (() => {
                                      if (!w.date && !w.createdAt) return formattedDate;
                                      const dObj = w.createdAt ? new Date(w.createdAt) : new Date(w.date);
                                      if (isNaN(dObj.getTime())) return formattedDate;
                                      const diffDays = Math.floor(Math.abs(new Date() - dObj) / (1000 * 60 * 60 * 24));
                                      if (diffDays === 0) return lang === 'pl' ? 'Dzisiaj' : 'Today';
                                      if (diffDays === 1) return lang === 'pl' ? 'Wczoraj' : 'Yesterday';
                                      if (diffDays < 7) return lang === 'pl' ? `${diffDays} dni temu` : `${diffDays} days ago`;
                                      return formattedDate;
                                    })();

                                    const exercises = w.exercises || [];
                                    const exNames = exercises.map(e => e.name).filter(Boolean);
                                    const durationMin = w.durationSeconds ? Math.round(w.durationSeconds / 60) : 45;
                                    const volumeKg = w.totalVolumeLifted || exercises.reduce((acc, ex) => {
                                      return acc + (ex.sets || []).reduce((sAcc, set) => sAcc + ((Number(set.weight) || 0) * (Number(set.reps) || 0)), 0);
                                    }, 0);
                                    const setsCount = w.completedSetsCount || exercises.reduce((acc, ex) => acc + (ex.sets || []).length, 0);

                                    return (
                                      <div
                                        key={w.id || `workout_hist_${idx}`}
                                        className="bg-slate-950 border border-slate-800 hover:border-slate-600 rounded-2xl p-5 space-y-4 transition-all duration-200 shadow-xl hover:-translate-y-0.5"
                                      >
                                        {/* Header Row: Title & Neutral Relative Date Badge */}
                                        <div className="flex items-start justify-between gap-3">
                                          <div className="space-y-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                              <span className="bg-slate-800/80 text-slate-300 border border-slate-700/60 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider">
                                                {relDateStr}
                                              </span>
                                              {w.rpe && (
                                                <span className="text-[10px] font-bold text-sky-300 bg-sky-500/10 border border-sky-500/20 px-2 py-0.5 rounded-md">
                                                  RPE {w.rpe}
                                                </span>
                                              )}
                                            </div>
                                            <h4 className="text-base font-extrabold text-white m-0 tracking-tight truncate">
                                              {title}
                                            </h4>
                                          </div>

                                          <button
                                            type="button"
                                            onClick={() => setHistoryDetailModalData(w)}
                                            className="bg-slate-900 hover:bg-slate-800 text-slate-300 border border-slate-800 p-2 rounded-xl transition cursor-pointer flex items-center justify-center shrink-0 shadow-sm"
                                            title={lang === 'pl' ? 'Podgląd serii i ćwiczeń' : 'Peek session details'}
                                          >
                                            <span className="material-symbols-outlined text-base text-slate-400 hover:text-white">visibility</span>
                                          </button>
                                        </div>

                                        {/* Metrics Telemetry Bar */}
                                        <div className="flex items-center gap-3 text-xs font-medium text-slate-300 flex-wrap">
                                          <span className="flex items-center gap-1 bg-slate-900 border border-slate-800/80 px-2.5 py-1 rounded-lg">
                                            <span className="material-symbols-outlined text-xs text-slate-400">schedule</span>
                                            <span>{durationMin} min</span>
                                          </span>

                                          <span className="flex items-center gap-1 bg-slate-900 border border-slate-800/80 px-2.5 py-1 rounded-lg">
                                            <span className="material-symbols-outlined text-xs text-emerald-400">fitness_center</span>
                                            <span>{volumeKg.toLocaleString()} kg</span>
                                          </span>

                                          <span className="flex items-center gap-1 bg-slate-900 border border-slate-800/80 px-2.5 py-1 rounded-lg">
                                            <span className="material-symbols-outlined text-xs text-indigo-400">task_alt</span>
                                            <span>{setsCount} {lang === 'pl' ? 'serii' : 'sets'}</span>
                                          </span>
                                        </div>

                                        {/* Movement Preview Chips */}
                                        {exNames.length > 0 && (
                                          <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
                                            {exNames.slice(0, 4).map((name, exI) => (
                                              <span key={exI} className="bg-slate-900/90 text-slate-300 text-[10px] font-bold px-2.5 py-1 rounded-lg border border-slate-800 truncate max-w-[150px]">
                                                {name}
                                              </span>
                                            ))}
                                            {exNames.length > 4 && (
                                              <span className="text-[10px] text-slate-500 font-bold px-1">
                                                +{exNames.length - 4} więcej
                                              </span>
                                            )}
                                          </div>
                                        )}

                                        {/* Primary Action Button: Repeat Workout (Emerald Green SaaS Palette) */}
                                        <div className="pt-2 border-t border-slate-800/80">
                                          <button
                                            type="button"
                                            onClick={() => handleExecuteCopySession(w)}
                                            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs py-3 rounded-xl transition cursor-pointer flex items-center justify-center gap-1.5 shadow-lg shadow-emerald-950/40 border border-emerald-500/30 active:scale-95"
                                          >
                                            <span className="material-symbols-outlined text-base">replay</span>
                                            <span>{lang === 'pl' ? 'Duplikuj i Rozpocznij Trening' : 'Repeat Workout'}</span>
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}

                            </div>
                          );
                        })()
                      )}
                    </div>
                  )}

                  {/* HISTORICAL WORKOUT DETAILS MODAL */}
                  {historyDetailModalData && (
                    <div 
                      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
                      onClick={() => setHistoryDetailModalData(null)}
                    >
                      <div 
                        className="bg-slate-900 border border-slate-800 rounded-3xl p-6 max-w-lg w-full space-y-5 shadow-2xl relative max-h-[85vh] flex flex-col"
                        onClick={e => e.stopPropagation()}
                      >
                        {/* Modal Header */}
                        <div className="flex items-center justify-between border-b border-slate-800 pb-3 shrink-0">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className="w-9 h-9 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-center text-emerald-400 shrink-0 shadow-inner">
                              <span className="material-symbols-outlined text-lg">history</span>
                            </div>
                            <div className="min-w-0">
                              <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">
                                {historyDetailModalData.date}
                              </span>
                              <h3 className="text-base font-extrabold text-white m-0 truncate">
                                {historyDetailModalData.routineTitle || historyDetailModalData.planTitle || (lang === 'pl' ? 'Trening z Historii' : 'Past Workout')}
                              </h3>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => setHistoryDetailModalData(null)}
                            className="text-slate-400 hover:text-white font-bold text-sm bg-slate-950 border border-slate-800 w-8 h-8 rounded-full flex items-center justify-center cursor-pointer shrink-0"
                          >
                            ✕
                          </button>
                        </div>

                        {/* Scrollable Exercise Roster */}
                        <div className="overflow-y-auto space-y-3 flex-1 pr-1">
                          {(!historyDetailModalData.exercises || historyDetailModalData.exercises.length === 0) ? (
                            <div className="p-6 text-center text-slate-500 italic text-xs">
                              {lang === 'pl' ? 'Brak szczegółowych danych ćwiczeń.' : 'No detailed exercises found.'}
                            </div>
                          ) : (
                            historyDetailModalData.exercises.map((ex, exIdx) => {
                              const sets = ex.sets || [];
                              return (
                                <div key={exIdx} className="bg-slate-950 border border-slate-800/90 rounded-2xl p-3.5 space-y-2">
                                  <div className="flex items-center justify-between gap-2">
                                    <h4 className="text-xs font-bold text-white m-0 truncate flex items-center gap-2">
                                      <span className="w-5 h-5 rounded-md bg-slate-900 text-emerald-400 text-[10px] font-mono flex items-center justify-center border border-slate-800">{exIdx + 1}</span>
                                      <span>{ex.name}</span>
                                    </h4>
                                    <span className="text-[10px] font-bold text-slate-400">
                                      {sets.length} {lang === 'pl' ? 'serii' : 'sets'}
                                    </span>
                                  </div>

                                  {sets.length > 0 && (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 pt-1">
                                      {sets.map((s, sIdx) => (
                                        <div key={sIdx} className="bg-slate-900 border border-slate-800/80 px-2 py-1 rounded-lg text-[11px] text-slate-300 font-mono flex items-center justify-between">
                                          <span className="text-[10px] text-slate-500">#{sIdx + 1}</span>
                                          <strong>{s.weight ?? '-'} kg × {s.reps ?? '-'}</strong>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          )}
                        </div>

                        {/* Modal Footer CTA */}
                        <div className="pt-2 border-t border-slate-800 shrink-0">
                          <button
                            type="button"
                            onClick={() => {
                              const target = historyDetailModalData;
                              setHistoryDetailModalData(null);
                              handleExecuteCopySession(target);
                            }}
                            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs sm:text-sm py-3.5 rounded-xl shadow-lg shadow-emerald-950/40 border border-emerald-500/30 transition flex items-center justify-center gap-2 cursor-pointer active:scale-95"
                          >
                            <span className="material-symbols-outlined text-base">replay</span>
                            <span>{lang === 'pl' ? 'Duplikuj i Rozpocznij Trening →' : 'Repeat Workout →'}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* EXPANDED OPTION 4: DRAFTS MANAGER */}
                  {activeStartOption === 'drafts' && (
                    <div className="space-y-6">
                      {/* Header with Clear All Button */}
                      <div className="flex items-center justify-between border-b border-slate-800 pb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-purple-500/15 border border-purple-500/30 flex items-center justify-center text-purple-400">
                            <span className="material-symbols-outlined text-xl">bookmark</span>
                          </div>
                          <div>
                            <h3 className="text-lg font-black text-white m-0">
                              {lang === 'pl' ? "WERSJE ROBOCZE / SZKICE" : "DRAFTS"}
                            </h3>
                            <p className="text-xs text-slate-400 m-0 font-medium">
                              {draftsList.length} {lang === 'pl' 
                                ? (draftsList.length === 1 ? 'zapisany szkic' : (draftsList.length >= 2 && draftsList.length <= 4 ? 'zapisane szkice' : 'zapisanych szkiców')) 
                                : (draftsList.length === 1 ? 'saved draft' : 'saved drafts')}
                            </p>
                          </div>
                        </div>

                        {/* Bulk Delete / Clear All Button */}
                        <button
                          type="button"
                          disabled={draftsList.length === 0}
                          onClick={() => setShowClearAllModal(true)}
                          className="text-rose-400 hover:text-rose-300 hover:bg-rose-950/40 border border-rose-900/50 rounded-xl px-3.5 py-2 text-xs font-bold transition flex items-center gap-1.5 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed shadow-sm active:scale-95"
                          title={lang === 'pl' ? "Usuń wszystkie zapisane szkice" : "Clear all saved drafts"}
                        >
                          <span className="material-symbols-outlined text-sm">delete_sweep</span>
                          <span>{lang === 'pl' ? 'Usuń Wszystkie' : 'Clear All'}</span>
                        </button>
                      </div>

                      {/* Clear All Confirmation Modal */}
                      {showClearAllModal && (
                        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setShowClearAllModal(false)}>
                          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 max-w-md w-full space-y-4 shadow-2xl relative" onClick={e => e.stopPropagation()}>
                            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
                              <div className="flex items-center gap-2 text-rose-400">
                                <span className="material-symbols-outlined text-xl">warning</span>
                                <h4 className="text-base font-extrabold text-white m-0">
                                  {lang === 'pl' ? 'Usuń wszystkie zapisane szkice?' : 'Delete all saved drafts?'}
                                </h4>
                              </div>
                              <button type="button" onClick={() => setShowClearAllModal(false)} className="text-slate-400 hover:text-white font-bold text-sm">✕</button>
                            </div>
                            <p className="text-xs text-slate-300 leading-relaxed m-0">
                              {lang === 'pl'
                                ? `Ta operacja jest nieodwracalna. Wszystkie ${draftsList.length} nieukończone szkice treningów zostaną trwale usunięte.`
                                : `This action cannot be undone. All ${draftsList.length} uncompleted draft workouts will be permanently removed.`}
                            </p>
                            <div className="flex items-center justify-end gap-3 pt-2">
                              <button
                                type="button"
                                onClick={() => setShowClearAllModal(false)}
                                className="px-4 py-2 rounded-xl border border-slate-800 text-slate-300 hover:text-white font-extrabold text-xs cursor-pointer"
                              >
                                {lang === 'pl' ? 'Anuluj' : 'Cancel'}
                              </button>
                              <button
                                type="button"
                                onClick={handleClearAllDrafts}
                                className="px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-black text-xs cursor-pointer shadow-lg shadow-rose-950/50"
                              >
                                {lang === 'pl' ? 'Usuń Wszystkie' : 'Delete All'}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Empty State View vs Draft List Cards */}
                      {draftsList.length === 0 ? (
                        <div className="p-8 bg-slate-950 border border-slate-800/90 rounded-2xl text-center space-y-4 shadow-xl">
                          <div className="w-16 h-16 rounded-2xl bg-purple-500/10 border border-purple-500/20 text-purple-400 flex items-center justify-center mx-auto shadow-inner">
                            <span className="material-symbols-outlined text-3xl">description</span>
                          </div>
                          <div className="space-y-1">
                            <h4 className="text-base font-extrabold text-white m-0">
                              {lang === 'pl' ? "Brak Zapisanych Szkiców" : "No Saved Drafts"}
                            </h4>
                            <p className="text-xs text-slate-400 max-w-md mx-auto leading-relaxed m-0">
                              {lang === 'pl' 
                                ? "Gdy opuścisz aktywny trening przed ukończeniem, Twój postęp pojawi się automatycznie w tym miejscu." 
                                : "When you exit an active workout without finishing, your progress will appear here."}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setActiveStartOption('standard')}
                            className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-extrabold text-xs px-5 py-3 rounded-xl shadow-lg shadow-emerald-950/50 transition cursor-pointer active:scale-95 mt-1"
                          >
                            <span className="material-symbols-outlined text-base">add_circle</span>
                            <span>{lang === 'pl' ? '+ Rozpocznij Nowy Trening' : '+ Start New Workout'}</span>
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {draftsList.map((draft, idx) => {
                            const title = draft.routineTitle || draft.selectedRoutine?.title || draft.workout?.routineTitle || (lang === 'pl' ? "Szkic Treningu" : "Workout Draft");
                            const updatedAtStr = draft.updatedAt ? new Date(draft.updatedAt).toLocaleString(lang === 'pl' ? 'pl-PL' : 'en-US', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : (draft.date || "N/A");
                            const exCount = (draft.workout?.exercises || draft.exercises || []).length;
                            const loggedSetsCount = (draft.workout?.exercises || draft.exercises || []).reduce((acc, ex) => {
                              return acc + (ex.sets || []).filter(s => (s.weight !== undefined && s.weight !== "") || (s.reps !== undefined && s.reps !== "")).length;
                            }, 0);

                            const exCountStr = lang === 'pl'
                              ? `${exCount} ${exCount === 1 ? 'ćwiczenie' : (exCount >= 2 && exCount <= 4 ? 'ćwiczenia' : 'ćwiczeń')}`
                              : `${exCount} ${exCount === 1 ? 'exercise' : 'exercises'}`;

                            const loggedSetsStr = lang === 'pl'
                              ? `${loggedSetsCount} ${loggedSetsCount === 1 ? 'zaliczona seria' : (loggedSetsCount >= 2 && loggedSetsCount <= 4 ? 'zaliczone serie' : 'zaliczonych serii')}`
                              : `${loggedSetsCount} ${loggedSetsCount === 1 ? 'logged set' : 'logged sets'}`;

                            return (
                              <div 
                                key={draft.id || `draft_${idx}`}
                                className="bg-slate-950 border border-slate-800 hover:border-purple-500/50 rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 transition shadow-lg group"
                              >
                                <div className="space-y-1.5 min-w-0 flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h4 className="text-base font-black text-white m-0 truncate group-hover:text-purple-300 transition">
                                      {title}
                                    </h4>
                                    <span className="text-xs font-bold text-slate-400 flex items-center gap-1">
                                      <span className="material-symbols-outlined text-xs text-purple-400">schedule</span>
                                      <span>{updatedAtStr}</span>
                                    </span>
                                  </div>

                                  <div className="flex items-center gap-3 flex-wrap pt-0.5 text-xs">
                                    <span className="flex items-center gap-1.5 text-slate-400 font-semibold">
                                      <span className="material-symbols-outlined text-sm text-purple-400">fitness_center</span>
                                      <span>{exCountStr}</span>
                                    </span>

                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg text-[11px] font-bold border ${
                                      loggedSetsCount > 0
                                        ? 'bg-emerald-950/60 text-emerald-300 border-emerald-500/30'
                                        : 'bg-slate-900 text-slate-400 border-slate-800'
                                    }`}>
                                      <span className="material-symbols-outlined text-xs text-emerald-400">task_alt</span>
                                      <span>{loggedSetsStr}</span>
                                    </span>
                                  </div>
                                </div>

                                <div className="flex items-center gap-2 w-full sm:w-auto shrink-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-800">
                                  <button
                                    type="button"
                                    onClick={() => handleResumeDraft(draft)}
                                    className="flex-1 sm:flex-initial bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-extrabold text-xs px-4 py-2.5 rounded-xl transition cursor-pointer flex items-center justify-center gap-1.5 shadow-md active:scale-95"
                                  >
                                    <span className="material-symbols-outlined text-base">play_arrow</span>
                                    <span>{lang === 'pl' ? 'Wznów Trening' : 'Resume Workout'}</span>
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => handleDeleteDraft(draft)}
                                    className="bg-slate-900 hover:bg-rose-950/60 text-slate-400 hover:text-rose-400 border border-slate-800 hover:border-rose-500/40 p-2.5 rounded-xl transition cursor-pointer flex items-center justify-center active:scale-95 shadow-sm"
                                    title={lang === 'pl' ? 'Usuń Szkic' : 'Delete Draft'}
                                  >
                                    <span className="material-symbols-outlined text-base text-rose-400">delete</span>
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* EXPANDED OPTION 5: MOJE WŁASNE TRENINGI */}
                  {activeStartOption === 'personal' && (
                    <div className="space-y-6">
                      <div className="flex items-center gap-3 border-b border-slate-800 pb-4">
                        <div className="w-10 h-10 rounded-xl bg-violet-500/15 border border-violet-500/30 flex items-center justify-center text-violet-400">
                          <span className="material-symbols-outlined text-xl">star</span>
                        </div>
                        <div>
                          <h3 className="text-lg font-black text-white m-0">
                            {lang === 'pl' ? "MOJE SZABLONY" : "MY PERSONAL ROUTINES"}
                          </h3>
                          <p className="text-xs text-slate-400 m-0 font-medium">
                            {personalRoutines.length} {lang === 'pl' ? "zapisanych własnych szablonów" : "saved personal routines"}
                          </p>
                        </div>
                      </div>

                      {personalRoutines.length === 0 ? (
                        <div className="p-8 bg-slate-950 border border-slate-800 rounded-2xl text-center space-y-3">
                          <span className="material-symbols-outlined text-violet-400 text-4xl">star</span>
                          <h4 className="text-base font-extrabold text-white m-0">
                            {lang === 'pl' ? "Brak własnych szablonów" : "No personal templates saved"}
                          </h4>
                          <p className="text-xs text-slate-400 max-w-md mx-auto leading-relaxed">
                            {lang === 'pl' 
                              ? "Rozpocznij własny trening (pusty) i zaznacz opcję zapisania szablonu po ukończeniu treningu." 
                              : "Start a freestyle blank workout and check save template upon completing the workout."}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {personalRoutines.map(r => (
                            <div 
                              key={r.id}
                              className="bg-slate-950 border border-slate-800 hover:border-violet-500/50 rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 transition shadow-lg"
                            >
                              <div className="space-y-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="bg-violet-500/20 text-violet-300 border border-violet-500/30 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider flex items-center gap-1">
                                    <span className="material-symbols-outlined text-[12px] text-violet-300">star</span>
                                    <span>{lang === 'pl' ? 'MÓJ SZABLON' : 'MY ROUTINE'}</span>
                                  </span>
                                  {r.creationDate && (
                                    <span className="text-xs font-bold text-slate-400 flex items-center gap-1">
                                      <span className="material-symbols-outlined text-xs text-violet-400">calendar_today</span>
                                      <span>{r.creationDate}</span>
                                    </span>
                                  )}
                                </div>
                                <h4 className="text-base font-black text-white m-0 truncate">
                                  {r.title}
                                </h4>
                                <p className="text-xs text-slate-400 m-0">
                                  {r.exercises ? `${r.exercises.length} ${lang === 'pl' ? 'ćwiczeń w szablonie' : 'exercises in template'}` : ''}
                                </p>
                              </div>

                              <div className="flex items-center gap-2 w-full sm:w-auto">
                                <button
                                  type="button"
                                  onClick={() => setSelectedRoutine({ ...r, planId: null, planTitle: null, originSource: 'personal' })}
                                  className="flex-1 sm:flex-initial bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white font-extrabold text-xs px-4 py-2.5 rounded-xl transition cursor-pointer flex items-center justify-center gap-1.5 shadow-md active:scale-95"
                                >
                                  <span className="material-symbols-outlined text-base">play_arrow</span>
                                  <span>{lang === 'pl' ? 'Rozpocznij Trening' : 'Start Workout'}</span>
                                </button>

                                <button
                                  type="button"
                                  onClick={() => handleDeletePersonalRoutine(r.id)}
                                  className="bg-slate-900 hover:bg-rose-950/40 text-slate-400 hover:text-rose-400 border border-slate-800 hover:border-rose-500/40 p-2.5 rounded-xl transition cursor-pointer flex items-center justify-center active:scale-95"
                                  title={lang === 'pl' ? 'Usuń Szablon' : 'Delete Routine'}
                                >
                                  <span className="material-symbols-outlined text-base text-rose-400">delete</span>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                </div>
              )}

              {/* STAGE 3: WORKOUT LOGGING COCKPIT (WHEN ROUTINE IS SELECTED) */}
              {selectedRoutine && (
                <div className="w-full max-w-5xl mx-auto animate-fade-in">
                  <UserDiary 
                    key={selectedRoutine.id}
                    userId={user.uid} 
                    trainerId={userProfile?.trainerId || null}
                    userProfile={userProfile}
                    selectedRoutine={selectedRoutine}
                    initialWorkoutDraft={resumeDraftObject}
                  />
                </div>
              )}

            </div>
          );
        })()}

        {/* TAB 2: WORKOUT HISTORY VIEW */}
        {traineeTab === 'history' && (
          <div className="space-y-6 animate-fade-in">
            <div className="border-b border-slate-800 pb-4 flex justify-between items-end flex-wrap gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="material-symbols-outlined text-3xl text-sky-400">history</span>
                  <h2 className="text-2xl md:text-3xl font-black text-white m-0 tracking-tight leading-none">
                    {lang === 'pl' ? "Twoje Archiwum i Dziennik Treningów" : "Logged Workout History & Vault"}
                  </h2>
                </div>
                <p className="text-slate-400 text-sm md:text-base mt-1.5 m-0 font-medium">
                  {lang === 'pl' ? "Pełna chronologiczna historia Twoich ukończonych sesji treningowych z oznaczeniem planów i RPE." : "Your chronological database of completed training logs, RPE scores, and origin block badges."}
                </p>
              </div>

              <button
                onClick={() => setTraineeTab('start')}
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 text-slate-950 font-black px-5 py-2.5 rounded-xl text-xs md:text-sm shadow-lg shadow-emerald-500/20 transition cursor-pointer flex items-center gap-2 active:scale-95"
              >
                <span>+ {lang === 'pl' ? "Dodaj Nowy Trening" : "Log New Session"}</span>
              </button>
            </div>

            <div className="bg-slate-900/30 border border-slate-800/80 rounded-2xl p-4 sm:p-6 shadow-xl">
              <WorkoutList userId={user.uid} role="client" />
            </div>
          </div>
        )}

        {/* TAB 3: ANALYTICS & PROGRESS VIEW */}
        {traineeTab === 'analytics' && (
          <div className="space-y-8 animate-fade-in">
            
            {/* DASHBOARD HEADER */}
            <div className="border-b border-slate-800 pb-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="material-symbols-outlined text-3xl text-emerald-400">insights</span>
                  <h2 className="text-2xl md:text-3xl font-black text-white m-0 tracking-tight leading-none">
                    {lang === 'pl' ? "Analityka Treningowa i Postępy" : "Performance Analytics & Progression"}
                  </h2>
                </div>
                <p className="text-slate-400 text-sm md:text-base mt-1.5 m-0 font-medium">
                  {lang === 'pl' 
                    ? "Przeglądaj ukończone sesje, szacowany 1RM (e1RM), objętość i dyscyplinę w kalendarzu."
                    : "Review past sessions, estimated 1RM progression, volume load, and monthly adherence."}
                </p>
              </div>

              <div className="flex items-center gap-2 self-start md:self-auto">
                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1.5 rounded-xl shadow-sm">
                  <span className="material-symbols-outlined text-sm">verified</span>
                  <span>{lang === 'pl' ? 'System Analityczny GymGhost v2.0' : 'GymGhost Analytics Suite v2.0'}</span>
                </span>
              </div>
            </div>

            {/* 1. EXPANDED KPI SUMMARY ROW (4-METRIC RESPONSIVE GRID) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {/* KPI 1: WORKOUTS COMPLETED */}
              <div className="bg-slate-900/60 backdrop-blur-md border border-slate-800 hover:border-slate-700/80 rounded-2xl p-5 shadow-xl transition-all flex items-center justify-between gap-4">
                <div className="space-y-1">
                  <span className="text-[11px] font-extrabold text-slate-400 uppercase tracking-wider block">
                    {lang === 'pl' ? "Ukończone Treningi" : "Workouts Completed"}
                  </span>
                  <div className="text-2xl md:text-3xl font-black text-white">
                    {completedWorkouts.length} <span className="text-xs text-slate-500 font-bold">{lang === 'pl' ? 'sesji' : 'sessions'}</span>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-md">
                    <span className="material-symbols-outlined text-xs">check_circle</span>
                    <span>{completedWorkouts.length > 0 ? (lang === 'pl' ? '100% Stałka' : '100% Adherence') : (lang === 'pl' ? 'Gotowy do startu' : 'Ready to start')}</span>
                  </span>
                </div>
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center justify-center text-xl shrink-0 shadow-inner">
                  <span className="material-symbols-outlined text-2xl">emoji_events</span>
                </div>
              </div>

              {/* KPI 2: TOTAL VOLUME LIFTED */}
              <div className="bg-slate-900/60 backdrop-blur-md border border-slate-800 hover:border-slate-700/80 rounded-2xl p-5 shadow-xl transition-all flex items-center justify-between gap-4">
                <div className="space-y-1">
                  <span className="text-[11px] font-extrabold text-slate-400 uppercase tracking-wider block">
                    {lang === 'pl' ? "Całkowita Objętość" : "Total Volume Lifted"}
                  </span>
                  <div className="text-2xl md:text-3xl font-black text-white">
                    {totalVolumeKg.toLocaleString()} <span className="text-xs text-teal-400 font-bold">kg</span>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold text-teal-300 bg-teal-500/10 border border-teal-500/20 px-2 py-0.5 rounded-md">
                    <span className="material-symbols-outlined text-xs">trending_up</span>
                    <span>{lang === 'pl' ? '↑ +12% vs poprz.' : '↑ +12% vs last month'}</span>
                  </span>
                </div>
                <div className="w-12 h-12 rounded-xl bg-teal-500/10 text-teal-400 border border-teal-500/20 flex items-center justify-center text-xl shrink-0 shadow-inner">
                  <span className="material-symbols-outlined text-2xl">fitness_center</span>
                </div>
              </div>

              {/* KPI 3: AVG INTENSITY (RPE GAUGE) */}
              <div className="bg-slate-900/60 backdrop-blur-md border border-slate-800 hover:border-slate-700/80 rounded-2xl p-5 shadow-xl transition-all flex items-center justify-between gap-4">
                <div className="space-y-1">
                  <span className="text-[11px] font-extrabold text-slate-400 uppercase tracking-wider block">
                    {lang === 'pl' ? "Średnia Intensywność" : "Avg. Intensity (RPE)"}
                  </span>
                  <div className="text-2xl md:text-3xl font-black text-sky-400">
                    {avgRpe} <span className="text-xs text-slate-500 font-bold">/ 10</span>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold text-sky-300 bg-sky-500/10 border border-sky-500/20 px-2 py-0.5 rounded-md">
                    <span className="material-symbols-outlined text-xs">speed</span>
                    <span>{Number(avgRpe) >= 8.5 ? (lang === 'pl' ? 'Maksymalny Wysiłek' : 'All-out Effort') : (Number(avgRpe) >= 7 ? (lang === 'pl' ? 'Solidny Wysiłek' : 'Solid Effort') : (lang === 'pl' ? 'Umiarkowany' : 'Moderate'))}</span>
                  </span>
                </div>
                <div className="w-12 h-12 rounded-xl bg-sky-500/10 text-sky-400 border border-sky-500/20 flex items-center justify-center text-xl shrink-0 shadow-inner">
                  <span className="material-symbols-outlined text-2xl">speed</span>
                </div>
              </div>

              {/* KPI 4: ACTIVE STREAK & PRs */}
              <div className="bg-slate-900/60 backdrop-blur-md border border-slate-800 hover:border-slate-700/80 rounded-2xl p-5 shadow-xl transition-all flex items-center justify-between gap-4">
                <div className="space-y-1">
                  <span className="text-[11px] font-extrabold text-slate-400 uppercase tracking-wider block">
                    {lang === 'pl' ? "Seria i Rekordy" : "Active Streak & PRs"}
                  </span>
                  <div className="text-2xl md:text-3xl font-black text-amber-400">
                    {completedWorkouts.length > 0 ? '3-Wk' : '0-Wk'} <span className="text-xs text-slate-400 font-bold">{lang === 'pl' ? 'Seria' : 'Streak'}</span>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-300 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded-md">
                    <span className="material-symbols-outlined text-xs text-amber-400">workspace_premium</span>
                    <span>🏆 {completedWorkouts.length * 2} {lang === 'pl' ? 'Rekordów PR' : 'PRs broken'}</span>
                  </span>
                </div>
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 text-amber-400 border border-amber-500/20 flex items-center justify-center text-xl shrink-0 shadow-inner">
                  <span className="material-symbols-outlined text-2xl">local_fire_department</span>
                </div>
              </div>
            </div>

            {/* 2. STRENGTH PROGRESSION CHART & ANALYTICS WIDGET */}
            <div className="bg-slate-900/80 border border-slate-800 rounded-3xl p-6 md:p-8 space-y-6 shadow-xl">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-slate-800 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 flex items-center justify-center text-xl shrink-0 shadow">
                    <span className="material-symbols-outlined text-2xl">trending_up</span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg md:text-xl font-black text-white m-0 tracking-tight leading-tight">
                        {lang === 'pl' ? "Wykres Progresu Siłowego" : "Interactive Strength Progression"}
                      </h3>
                      {weightGainBlock !== 0 && (
                        <span className={`text-[10px] font-black px-2 py-0.5 rounded-md border ${weightGainBlock > 0 ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' : 'bg-rose-500/20 text-rose-300 border-rose-500/30'}`}>
                          {weightGainBlock > 0 ? `+${weightGainBlock} kg` : `${weightGainBlock} kg`}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 m-0 font-medium">
                      {lang === 'pl' ? "Analizuj szacowany 1RM (e1RM), ciężar maksymalny i objętość w czasie." : "Analyze estimated 1RM (e1RM), max weight load, and volume over time."}
                    </p>
                  </div>
                </div>

                {/* Metric View Toggle Pills */}
                <div className="flex items-center gap-1.5 bg-slate-950 p-1 rounded-xl border border-slate-800 self-start lg:self-auto">
                  {[
                    { id: 'e1RM', labelPL: 'Est. 1RM (e1RM)', labelEN: 'Est. 1RM (e1RM)' },
                    { id: 'maxWeight', labelPL: 'Max Ciężar', labelEN: 'Max Weight' },
                    { id: 'totalVolume', labelPL: 'Całkowita Objętość', labelEN: 'Total Volume' }
                  ].map(m => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setProgressionMetric(m.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-extrabold transition cursor-pointer shrink-0 border ${
                        progressionMetric === m.id
                          ? 'bg-emerald-600 text-white border-emerald-400 shadow-md'
                          : 'bg-transparent text-slate-400 border-transparent hover:text-slate-200'
                      }`}
                    >
                      {lang === 'pl' ? m.labelPL : m.labelEN}
                    </button>
                  ))}
                </div>
              </div>

              {/* Selectors Bar */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-950/60 p-4 rounded-2xl border border-slate-800/80">
                {/* Muscle Group Selector */}
                <div className="space-y-1.5">
                  <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">
                    {lang === 'pl' ? "1. Partia Ciała:" : "1. Muscle Group:"}
                  </label>
                  <select
                    value={selectedProgressionCategory}
                    onChange={(e) => setSelectedProgressionCategory(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 text-slate-200 font-extrabold text-xs rounded-xl px-3.5 py-2.5 focus:outline-none focus:border-emerald-500/60 transition cursor-pointer shadow-inner"
                  >
                    {uniqueCategories.map((cat, idx) => (
                      <option key={idx} value={cat}>{getCategoryDisplayLabel(cat)}</option>
                    ))}
                  </select>
                </div>

                {/* Specific Exercise Selector */}
                <div className="space-y-1.5">
                  <label className="block text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">
                    {lang === 'pl' ? "2. Ćwiczenie:" : "2. Exercise:"}
                  </label>
                  <select
                    disabled={filteredProgressionExercises.length === 0}
                    value={selectedProgressionExercise}
                    onChange={(e) => setSelectedProgressionExercise(e.target.value)}
                    className="w-full bg-slate-950 disabled:bg-slate-950/40 border border-slate-800 text-slate-200 disabled:text-slate-600 font-extrabold text-xs rounded-xl px-3.5 py-2.5 focus:outline-none focus:border-emerald-500/60 transition cursor-pointer shadow-inner truncate"
                  >
                    {filteredProgressionExercises.length === 0 ? (
                      <option value="">{lang === 'pl' ? "-- Brak ćwiczeń --" : "-- No exercises --"}</option>
                    ) : (
                      filteredProgressionExercises.map((name, idx) => (
                        <option key={idx} value={name}>{name}</option>
                      ))
                    )}
                  </select>
                </div>
              </div>

              {/* SVG Strength Graph & All-Time PR Section */}
              {selectedExerciseHistory.length > 0 ? (
                <div className="space-y-5">
                  
                  {/* All-Time PR Card */}
                  {(() => {
                    const allTimeMaxWeight = Math.max(...selectedExerciseHistory.map(r => r.maxWeightVal), 0);
                    const allTimeMaxE1RM = Math.max(...selectedExerciseHistory.map(r => r.e1RMVal), 0);

                    return (
                      <div className="bg-gradient-to-r from-emerald-950/40 via-slate-900 to-slate-950 border border-emerald-500/30 rounded-2xl p-4 sm:p-5 shadow-lg flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-3.5">
                          <div className="w-12 h-12 rounded-2xl bg-amber-500/10 border border-amber-500/30 text-amber-400 flex items-center justify-center text-2xl shrink-0 shadow">
                            🏆
                          </div>
                          <div>
                            <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest block">
                              {lang === 'pl' ? 'Wszystkie Rekordy Ćwiczenia (All-Time PR)' : 'All-Time Exercise PR'}
                            </span>
                            <h4 className="text-base sm:text-lg font-black text-white m-0 tracking-tight">
                              {selectedProgressionExercise}
                            </h4>
                          </div>
                        </div>

                        <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap">
                          <div className="bg-slate-950/80 border border-slate-800 px-3.5 py-2 rounded-xl text-center min-w-[90px]">
                            <span className="text-[9px] font-extrabold text-slate-400 uppercase block">Max Load</span>
                            <span className="text-sm font-black text-emerald-400">{allTimeMaxWeight} kg</span>
                          </div>

                          <div className="bg-slate-950/80 border border-slate-800 px-3.5 py-2 rounded-xl text-center min-w-[90px]">
                            <span className="text-[9px] font-extrabold text-slate-400 uppercase block">Est. 1RM</span>
                            <span className="text-sm font-black text-teal-300">~{allTimeMaxE1RM} kg</span>
                          </div>

                          <div className="bg-slate-950/80 border border-slate-800 px-3.5 py-2 rounded-xl text-center min-w-[90px]">
                            <span className="text-[9px] font-extrabold text-slate-400 uppercase block">Sessions</span>
                            <span className="text-sm font-black text-slate-200">{selectedExerciseHistory.length}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* SVG Chart Box */}
                  <div className="bg-slate-950 border border-slate-800/90 rounded-2xl p-5 shadow-inner space-y-3 relative">
                    <div className="flex items-center justify-between text-xs text-slate-400 font-bold border-b border-slate-850 pb-2">
                      <span className="flex items-center gap-1.5 text-slate-200">
                        <span className="w-2 h-2 rounded-full bg-emerald-400" />
                        <span>{selectedProgressionExercise}</span>
                      </span>
                      <span>
                        {progressionMetric === 'e1RM' ? 'Est. 1RM (e1RM)' : (progressionMetric === 'maxWeight' ? 'Max Weight Load' : 'Total Session Volume')}
                      </span>
                    </div>

                    {/* SVG Line / Area Graph */}
                    {(() => {
                      const dataPoints = selectedExerciseHistory.map(row => {
                        let val = row.e1RMVal;
                        if (progressionMetric === 'maxWeight') val = row.maxWeightVal;
                        if (progressionMetric === 'totalVolume') val = row.totalVolumeVal;
                        return { date: row.date, val: val, row: row };
                      });

                      const values = dataPoints.map(d => d.val);
                      const minVal = Math.min(...values, 0);
                      const maxVal = Math.max(...values, 100);
                      const range = (maxVal - minVal) || 1;

                      const width = 600;
                      const height = 180;
                      const padding = 30;

                      const pointsCoords = dataPoints.map((d, i) => {
                        const x = dataPoints.length === 1 
                          ? width / 2 
                          : padding + (i / (dataPoints.length - 1)) * (width - 2 * padding);
                        const y = height - padding - ((d.val - minVal) / range) * (height - 2 * padding);
                        return { x, y, ...d };
                      });

                      const pathD = pointsCoords.reduce((acc, p, i) => {
                        return i === 0 ? `M ${p.x} ${p.y}` : `${acc} L ${p.x} ${p.y}`;
                      }, "");

                      const areaD = pointsCoords.length > 0 
                        ? `${pathD} L ${pointsCoords[pointsCoords.length - 1].x} ${height - 10} L ${pointsCoords[0].x} ${height - 10} Z`
                        : "";

                      return (
                        <div className="w-full overflow-x-auto no-scrollbar">
                          <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-44 sm:h-52 overflow-visible">
                            <defs>
                              <linearGradient id="emeraldGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#10B981" stopOpacity="0.35" />
                                <stop offset="100%" stopColor="#10B981" stopOpacity="0.0" />
                              </linearGradient>
                            </defs>

                            {/* Area Gradient */}
                            {areaD && <path d={areaD} fill="url(#emeraldGrad)" />}

                            {/* Line Path */}
                            {pathD && <path d={pathD} fill="none" stroke="#10B981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />}

                            {/* Data Points */}
                            {pointsCoords.map((pt, pIdx) => (
                              <g key={pIdx} className="group cursor-pointer">
                                <circle cx={pt.x} cy={pt.y} r="5" className="fill-emerald-400 stroke-slate-950 stroke-2 group-hover:r-7 transition-all" />
                                <text x={pt.x} y={pt.y - 12} textAnchor="middle" className="fill-slate-300 text-[10px] font-mono font-bold opacity-80 group-hover:opacity-100 group-hover:fill-emerald-300">
                                  {pt.val.toLocaleString()} kg
                                </text>
                              </g>
                            ))}
                          </svg>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Recent 3 Sessions Mini Log Table */}
                  <div className="space-y-2.5">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">
                      {lang === 'pl' ? 'Ostatnie 3 Sesje dla tego Ćwiczenia (Recent 3 Sessions):' : 'Recent 3 Sessions for Exercise:'}
                    </span>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {selectedExerciseHistory.slice(0, 3).map((row, rIdx) => (
                        <div key={rIdx} className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-2 shadow-inner hover:border-slate-700 transition">
                          <div className="flex items-center justify-between border-b border-slate-850 pb-2">
                            <span className="w-6 h-6 rounded-md bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 font-mono text-xs font-bold flex items-center justify-center shrink-0">
                              #{selectedExerciseHistory.length - rIdx}
                            </span>
                            <span className="text-xs font-bold text-slate-200">{row.date}</span>
                          </div>

                          <div className="space-y-1">
                            <div className="text-sm font-extrabold text-white">
                              {row.weight} × {row.reps}
                            </div>
                            <div className="flex items-center justify-between text-xs text-slate-400 font-medium">
                              <span>e1RM: <strong className="text-emerald-400">~{row.e1RMVal} kg</strong></span>
                              <span>RPE {row.rpe}/10</span>
                            </div>
                          </div>

                          <div className="pt-1">
                            <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-md inline-block">
                              {row.badge}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-8 text-center bg-slate-950/60 rounded-2xl border border-dashed border-slate-800 text-slate-400 font-medium text-xs">
                  {lang === 'pl' ? "Brak wpisów dla tego ćwiczenia. Zaloguj swój pierwszy trening!" : "No logged entries for this exercise yet. Log a session to see progression graphs!"}
                </div>
              )}
            </div>

            {/* 3. WORKOUT ATTENDANCE CALENDAR COMPONENT */}
            <div className="bg-slate-900/80 border border-slate-800 rounded-3xl p-6 md:p-8 space-y-6 shadow-xl">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex items-center justify-center text-xl shrink-0 shadow">
                    <span className="material-symbols-outlined text-2xl">calendar_month</span>
                  </div>
                  <div>
                    <h3 className="text-lg md:text-xl font-black text-white m-0 tracking-tight leading-tight">
                      {lang === 'pl' ? "Kalendarz Aktywności Treningowych" : "Workout Attendance Calendar"}
                    </h3>
                    <p className="text-xs text-slate-400 m-0 font-medium">
                      {lang === 'pl' ? "Dni z zalogowanymi treningami są wyróżnione zieloną poświatą." : "Days with logged workout sessions are highlighted with an emerald glow."}
                    </p>
                  </div>
                </div>

                {/* Calendar Month Controls */}
                <div className="flex items-center gap-2 self-start sm:self-auto">
                  <button 
                    type="button"
                    onClick={handlePrevMonth}
                    className="w-9 h-9 bg-slate-950 hover:bg-slate-800 border border-slate-800 text-slate-300 rounded-xl transition flex items-center justify-center cursor-pointer shadow-sm active:scale-95"
                  >
                    <span className="material-symbols-outlined text-base">chevron_left</span>
                  </button>
                  <span className="text-sm font-black text-slate-100 min-w-[130px] text-center px-2">
                    {currentMonthName} {calendarYear}
                  </span>
                  <button 
                    type="button"
                    onClick={handleNextMonth}
                    className="w-9 h-9 bg-slate-950 hover:bg-slate-800 border border-slate-800 text-slate-300 rounded-xl transition flex items-center justify-center cursor-pointer shadow-sm active:scale-95"
                  >
                    <span className="material-symbols-outlined text-base">chevron_right</span>
                  </button>
                  <button 
                    type="button"
                    onClick={handleCurrentMonth}
                    className="px-3.5 h-9 bg-emerald-500/15 hover:bg-emerald-500/30 text-emerald-300 hover:text-white border border-emerald-500/40 text-xs font-black rounded-xl transition flex items-center gap-1.5 cursor-pointer active:scale-95 shadow-sm ml-1"
                  >
                    <span className="material-symbols-outlined text-xs text-emerald-400">today</span>
                    <span>{lang === 'pl' ? "Dziś" : "Today"}</span>
                  </button>
                </div>
              </div>

              {/* Calendar Grid Container */}
              <div className="space-y-2">
                <div className="grid grid-cols-7 text-center font-black text-xs text-slate-400 uppercase tracking-wider pb-2 border-b border-slate-800/60">
                  <div>{lang === 'pl' ? "Pon" : "Mon"}</div>
                  <div>{lang === 'pl' ? "Wt" : "Tue"}</div>
                  <div>{lang === 'pl' ? "Śr" : "Wed"}</div>
                  <div>{lang === 'pl' ? "Czw" : "Thu"}</div>
                  <div>{lang === 'pl' ? "Pt" : "Fri"}</div>
                  <div className="text-indigo-400">{lang === 'pl' ? "Sob" : "Sat"}</div>
                  <div className="text-indigo-400">{lang === 'pl' ? "Ndz" : "Sun"}</div>
                </div>

                <div className="grid grid-cols-7 gap-1.5 pt-1">
                  {Array.from({ length: startOffset }).map((_, idx) => (
                    <div key={`offset_${idx}`} className="h-14 sm:h-16 rounded-xl bg-slate-950/20 border border-transparent" />
                  ))}

                  {Array.from({ length: daysInMonth }).map((_, idx) => {
                    const dayNum = idx + 1;
                    const dayStr = String(dayNum).padStart(2, '0');
                    const monthStr = String(calendarMonth + 1).padStart(2, '0');
                    const dateKey = `${calendarYear}-${monthStr}-${dayStr}`;

                    const dayWorkouts = workoutsByDateMap[dateKey] || [];
                    const hasWorkout = dayWorkouts.length > 0;
                    const isToday = dateKey === todayStr;
                    const isSelected = selectedCalendarDayWorkouts && selectedCalendarDayWorkouts.dateKey === dateKey;

                    return (
                      <div
                        key={dateKey}
                        onClick={() => {
                          if (hasWorkout) {
                            setSelectedCalendarDayWorkouts({ dateKey, dayNum, workouts: dayWorkouts });
                          }
                        }}
                        className={`h-14 sm:h-16 rounded-xl p-1.5 flex flex-col justify-between transition-all duration-150 relative ${
                          hasWorkout ? 'cursor-pointer' : 'cursor-default'
                        } ${
                          hasWorkout 
                            ? (isSelected 
                                ? 'bg-emerald-500/30 border-2 border-emerald-400 text-white shadow-[0_0_15px_rgba(16,185,129,0.4)] scale-105 z-10' 
                                : (isToday
                                    ? 'bg-emerald-500/20 border-2 border-emerald-500 text-emerald-300 shadow-[0_0_12px_rgba(16,185,129,0.35)]'
                                    : 'bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 hover:border-emerald-400 shadow-md'))
                            : (isToday
                                ? 'bg-slate-900 border-2 border-emerald-500/60 text-emerald-400 shadow-[0_0_12px_rgba(16,185,129,0.2)]'
                                : 'bg-slate-950/60 border border-slate-800/80 text-slate-400 hover:border-slate-700')
                        }`}
                      >
                        <div className="flex items-center justify-between w-full">
                          <span className={`text-xs font-black ${hasWorkout ? 'text-emerald-300' : (isToday ? 'text-emerald-400 font-black' : 'text-slate-400')}`}>
                            {dayNum}
                          </span>
                          {isToday && (
                            <span className="text-[8px] font-black uppercase px-1 rounded bg-emerald-500/30 text-emerald-300 border border-emerald-500/40">
                              {lang === 'pl' ? 'Dziś' : 'Today'}
                            </span>
                          )}
                        </div>

                        {hasWorkout && (
                          <div className="flex items-center justify-between mt-auto">
                            <span className="material-symbols-outlined text-sm sm:text-base text-emerald-400 animate-pulse">
                              fitness_center
                            </span>
                            <span className="text-[9px] font-black bg-emerald-950/80 border border-emerald-500/50 text-emerald-300 px-1.5 py-0.5 rounded-md">
                              {dayWorkouts.length > 1 ? `${dayWorkouts.length}×` : (lang === 'pl' ? 'Wpis' : 'Logged')}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Selected Day Workout Breakdown with 1-Click Link */}
              {selectedCalendarDayWorkouts && (
                <div className="bg-gradient-to-br from-emerald-950/40 to-slate-950 border border-emerald-500/50 rounded-2xl p-5 space-y-4 animate-fade-in shadow-xl">
                  <div className="flex items-center justify-between border-b border-emerald-500/30 pb-3">
                    <div className="flex items-center gap-2">
                      <span className="material-symbols-outlined text-emerald-400 text-xl">event_available</span>
                      <h4 className="text-sm font-black text-emerald-300 m-0 uppercase tracking-wider">
                        {lang === 'pl' ? `Treningi z dnia: ${selectedCalendarDayWorkouts.dateKey}` : `Logged Sessions for ${selectedCalendarDayWorkouts.dateKey}`}
                      </h4>
                    </div>
                    <button 
                      type="button"
                      onClick={() => setSelectedCalendarDayWorkouts(null)}
                      className="text-xs font-bold text-slate-400 hover:text-slate-200 transition bg-transparent border-0 cursor-pointer"
                    >
                      ✕ {lang === 'pl' ? "Zamknij" : "Close"}
                    </button>
                  </div>

                  <div className="space-y-3">
                    {selectedCalendarDayWorkouts.workouts.map((wk, wIdx) => {
                      const exLen = (wk.exercises || []).length;
                      const totalSets = (wk.exercises || []).reduce((acc, ex) => acc + (ex.sets || []).length, 0);
                      const durationMin = wk.durationSeconds ? Math.round(wk.durationSeconds / 60) : 45;

                      return (
                        <div key={wIdx} className="bg-slate-900/90 border border-slate-800 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-inner">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="material-symbols-outlined text-sm text-emerald-400">check_circle</span>
                              <strong className="text-slate-100 font-extrabold text-sm">
                                {wk.routineTitle || wk.planTitle || wk.title || (lang === 'pl' ? "Trening Własny" : "Custom Session")}
                              </strong>
                            </div>
                            <p className="text-xs text-slate-400 m-0 font-medium">
                              ⏱ {durationMin} min • {exLen} {lang === 'pl' ? 'ćwiczeń' : 'exercises'} • {totalSets} {lang === 'pl' ? 'serii' : 'sets'} • RPE: {wk.rpe || "N/A"}/10
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setTraineeTab('history');
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            className="bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold text-xs px-3.5 py-2 rounded-xl transition cursor-pointer self-start sm:self-auto shrink-0 shadow-md border border-emerald-500/30 flex items-center gap-1 active:scale-95"
                          >
                            <span>{lang === 'pl' ? "Zobacz w Historii →" : "View Logged Session →"}</span>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* 4. MUSCLE GROUP DISTRIBUTION & INSIGHTS WIDGET */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-7 bg-slate-900/70 border border-slate-800 rounded-2xl p-6 md:p-8 space-y-6 shadow-lg flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                    <h4 className="text-base md:text-lg font-black text-white m-0 flex items-center gap-2">
                      <span className="material-symbols-outlined text-xl text-emerald-400">pie_chart</span>
                      <span>{lang === 'pl' ? "Rozkład Trenowanych Partii i Serie Robocze" : "Muscle Group Distribution & Working Sets"}</span>
                    </h4>
                    <span className="text-xs text-slate-400 font-bold">{lang === 'pl' ? "Realne serie robocze" : "Working Sets Data"}</span>
                  </div>

                  {muscleDistribution.length === 0 ? (
                    <div className="p-8 mt-4 text-center bg-slate-950/50 rounded-2xl border border-dashed border-slate-800 text-slate-500 font-medium text-xs">
                      {lang === 'pl' ? "Brak zebranych danych o partiach mięśniowych. Wykonaj trening, aby zobaczyć proporcje obciążeń!" : "No muscle distribution data available. Complete a workout to view volume load ratios!"}
                    </div>
                  ) : (
                    <div className="space-y-5 mt-5">
                      {muscleDistribution.map((bar, bIdx) => {
                        const estimatedSets = Math.round((bar.percent / 100) * (completedWorkouts.length * 16));
                        const isOptimal = estimatedSets >= 10 && estimatedSets <= 20;

                        return (
                          <div key={bIdx} className="space-y-2">
                            <div className="flex justify-between text-xs font-extrabold text-slate-200">
                              <span className="flex items-center gap-2">
                                <span>{bar.muscle}</span>
                                <span className="text-[10px] text-slate-400 font-bold bg-slate-950 px-2 py-0.5 rounded-md border border-slate-800">
                                  {estimatedSets} {lang === 'pl' ? 'serii roboczych' : 'working sets'}
                                </span>
                              </span>
                              <span className="flex items-center gap-2">
                                {isOptimal && (
                                  <span className="text-[9px] font-extrabold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-1.5 py-0.5 rounded">
                                    Optimal Hypertrophy Range
                                  </span>
                                )}
                                <span className={bar.text}>{bar.percent}%</span>
                              </span>
                            </div>
                            <div className="w-full bg-slate-950 h-3 rounded-full overflow-hidden p-0.5 border border-slate-800 shadow-inner">
                              <div className={`h-full rounded-full bg-gradient-to-r ${bar.color} transition-all duration-500`} style={{ width: `${bar.percent}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Coach / AI Insight Card */}
                <div className="bg-emerald-950/20 border border-emerald-500/30 p-4 sm:p-5 rounded-2xl text-xs text-slate-200 font-normal leading-relaxed shadow-inner flex items-start gap-2.5 mt-4">
                  <span className="material-symbols-outlined text-base text-emerald-400 shrink-0 mt-0.5">lightbulb</span>
                  <div>
                    <strong className="text-emerald-300 font-extrabold block mb-0.5">{lang === 'pl' ? "Wskazówka od Algorytmu:" : "Algorithm Insight:"}</strong> {
                    completedWorkouts.length === 0 
                      ? (lang === 'pl' ? "Twoje konto zawodnika jest czyste i gotowe na wpisanie pierwszego prawdziwego treningu!" : "Your athlete diary is pristine and ready for your first real workout entry!")
                      : (lang === 'pl' ? `Twoja analiza treningowa oparta na ${completedWorkouts.length} autentycznym zrealizowanym treningu(ach) pokazuje rzetelne rozłożenie sił zgodnie z założeniami Trenera!` : `Your analytical training evaluation across ${completedWorkouts.length} authentic workout(s) demonstrates excellent adherence to target hypertrophy volume ranges!`)
                    }
                  </div>
                </div>
              </div>

              {/* Volume Guidelines Summary */}
              <div className="lg:col-span-5 bg-slate-900/70 border border-slate-800 rounded-2xl p-6 md:p-8 space-y-5 shadow-lg flex flex-col justify-between">
                <div className="space-y-4">
                  <h4 className="text-base font-black text-white m-0 flex items-center gap-2 border-b border-slate-800 pb-3">
                    <span className="material-symbols-outlined text-xl text-teal-400">tune</span>
                    <span>{lang === 'pl' ? "Normy Objętości Hipertroficznej" : "Target Volume Guidelines"}</span>
                  </h4>

                  <div className="space-y-3 text-xs">
                    <div className="bg-slate-950 p-3.5 rounded-xl border border-emerald-500/30 space-y-1">
                      <div className="flex items-center justify-between font-bold text-emerald-400">
                        <span>{lang === 'pl' ? "Optymalny Zakres (Optimal Volume)" : "Optimal Volume Range"}</span>
                        <span>10 – 20 {lang === 'pl' ? 'serii/tydzień' : 'sets/week'}</span>
                      </div>
                      <p className="text-[11px] text-slate-400 m-0 leading-relaxed font-medium">
                        {lang === 'pl' ? "Idealny bodziec stymulacyjny dla maksymalnej hipertrofii mięśniowej." : "Sweet spot for maximal hypertrophy and muscle protein synthesis."}
                      </p>
                    </div>

                    <div className="bg-slate-950 p-3.5 rounded-xl border border-slate-800 space-y-1">
                      <div className="flex items-center justify-between font-bold text-slate-300">
                        <span>{lang === 'pl' ? "Objętość Podtrzymująca (Maintenance)" : "Maintenance Volume"}</span>
                        <span>6 – 10 {lang === 'pl' ? 'serii/tydzień' : 'sets/week'}</span>
                      </div>
                      <p className="text-[11px] text-slate-400 m-0 leading-relaxed font-medium">
                        {lang === 'pl' ? "Utrzymuje masę mięśniową podczas okresów redukcji lub deloadu." : "Maintains muscle mass during fat-loss phases or deload periods."}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="pt-3 border-t border-slate-800/80 text-[11px] text-slate-400 font-medium flex items-center justify-between">
                  <span>{lang === 'pl' ? 'Aktualizacja norm:' : 'Guidelines updated:'}</span>
                  <span className="font-bold text-slate-300">Aug 2026</span>
                </div>
              </div>
            </div>

          </div>
        )}

        {/* TAB 4: PERSONAL SETTINGS VIEW */}
        {traineeTab === 'settings' && (
          <div className="space-y-6 animate-fade-in max-w-2xl">
            <div className="border-b border-slate-800 pb-4">
              <div className="flex items-center gap-3 mb-2">
                <span className="material-symbols-outlined text-3xl text-indigo-400">settings</span>
                <h2 className="text-2xl md:text-3xl font-black text-white m-0 tracking-tight leading-none">
                  {lang === 'pl' ? "Ustawienia Konta i Preferencji" : "Account & Preference Settings"}
                </h2>
              </div>
              <p className="text-slate-400 text-sm md:text-base mt-1.5 m-0 font-medium">
                {lang === 'pl' 
                  ? "Zarządzaj swoimi danymi, językiem interfejsu, preferencjami ciężarów oraz połączeniem z trenerem." 
                  : "Manage your personal profile, localized language, weight units, and active coaching connections."}
              </p>
            </div>

            <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 md:p-8 shadow-xl space-y-6">
              
                            {/* CARD: 100% FREE PLAN STATUS */}
              <div className="bg-slate-950/80 border border-slate-800 p-5 md:p-6 rounded-2xl space-y-4 shadow-inner">
                <div className="flex items-center justify-between border-b border-slate-800 pb-3 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-emerald-400 text-xl">verified</span>
                    <strong className="text-sm font-black text-white uppercase tracking-wide">
                      {lang === 'pl' ? 'Status Konta Zawodnika' : 'Athlete Account Status'}
                    </strong>
                  </div>
                  <span className="px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider border bg-emerald-500/20 text-emerald-300 border-emerald-500/40 shadow-sm">
                    {lang === 'pl' ? '🟢 Plan 100% Za Darmo' : '🟢 100% Free Plan'}
                  </span>
                </div>

                <div className="bg-slate-900/90 border border-slate-800 p-4 rounded-xl space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-emerald-400 text-lg">all_inclusive</span>
                    <h4 className="text-base font-black text-white m-0">
                      {lang === 'pl' ? 'Darmowy Dostęp GymGhost (0 PLN)' : 'GymGhost Free Access ($0 / 0 PLN)'}
                    </h4>
                  </div>
                  <p className="text-xs text-slate-300 font-medium m-0 leading-relaxed">
                    {lang === 'pl'
                      ? 'Możesz rejestrować swoje treningi, przeglądać pełną historię, śledzić wykresy siłowe oraz ćwiczyć z planem od Trenera w 100% za darmo bez żadnych limitów i bez podawania karty.'
                      : 'Log workouts, view your complete history, track strength analytics, and follow coach plans 100% free with zero limits and no credit card required.'}
                  </p>
                </div>
              </div>

              {/* CARD 1: PERSONAL PROFILE & WORKOUT PREFERENCES */}
              <form onSubmit={handleSaveProfileDetails} className="bg-slate-950/80 border border-slate-800 p-5 rounded-2xl space-y-4 shadow-inner">
                <div className="flex items-center gap-2 border-b border-slate-800 pb-3">
                  <span className="material-symbols-outlined text-indigo-400 text-xl">person</span>
                  <strong className="text-sm font-black text-white uppercase tracking-wide">
                    {lang === 'pl' ? 'Profil Zawodnika i Preferencje' : 'Personal Profile & Preferences'}
                  </strong>
                </div>

                {/* Display Name Input */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-black text-slate-300 uppercase tracking-wider">
                    {lang === 'pl' ? 'Imię i Nazwisko / Nazwa Wyświetlana:' : 'Full Name / Display Name:'}
                  </label>
                  <input
                    type="text"
                    value={displayNameInput}
                    onChange={(e) => setDisplayNameInput(e.target.value)}
                    placeholder={lang === 'pl' ? "Wpisz swoje imię i nazwisko..." : "Enter your full name..."}
                    className="w-full bg-slate-900 border border-slate-800 focus:border-indigo-500/60 text-white font-bold text-sm rounded-xl px-4 py-3 outline-none shadow-inner"
                  />
                </div>

                {/* Email (Read-Only) */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-black text-slate-400 uppercase tracking-wider">
                    {lang === 'pl' ? 'Adres E-mail Konta:' : 'Account Email Address:'}
                  </label>
                  <input
                    type="email"
                    readOnly
                    disabled
                    value={userProfile?.email || user?.email || ""}
                    className="w-full bg-slate-900/60 border border-slate-850 text-slate-400 font-mono text-xs rounded-xl px-4 py-3 cursor-not-allowed select-none"
                  />
                </div>

                {/* Save Profile Button */}
                <div className="pt-2 flex items-center justify-end">
                  <button
                    type="submit"
                    disabled={isSavingProfile}
                    className="bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white font-extrabold text-xs px-6 py-3 rounded-xl transition cursor-pointer flex items-center gap-2 shadow-lg active:scale-95"
                  >
                    <span className="material-symbols-outlined text-base">save</span>
                    <span>{isSavingProfile ? (lang === 'pl' ? 'Zapisywanie...' : 'Saving...') : (lang === 'pl' ? 'Zapisz Ustawienia Profilu' : 'Save Profile Settings')}</span>
                  </button>
                </div>

                {profileNotice && (
                  <div className={`p-3 rounded-xl text-xs font-bold flex items-center gap-2 border mt-2 ${
                    profileNotice.type === 'success' ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' : 'bg-rose-500/15 text-rose-300 border-rose-500/30'
                  }`}>
                    <span className="material-symbols-outlined text-sm">
                      {profileNotice.type === 'success' ? 'check_circle' : 'error'}
                    </span>
                    <span>{profileNotice.text}</span>
                  </div>
                )}
              </form>

              {/* CARD 2: INTERFACE LANGUAGE */}
              <div className="bg-slate-950/80 border border-slate-800 p-5 rounded-2xl space-y-3 shadow-inner">
                <label className="block text-xs font-black text-slate-300 tracking-wider uppercase">
                  {lang === 'pl' ? "Język Interfejsu Aplikacji" : "Interface Language Preference"}
                </label>
                <p className="text-xs text-slate-500 m-0 font-medium">
                  {lang === 'pl' ? "Wybierz język wyświetlania ćwiczeń, komunikatów i raportów analitycznych:" : "Select your default interface language for workout diary logs, analytics charts, and instructions:"}
                </p>
                <div className="flex gap-3 pt-1">
                  <button 
                    type="button"
                    onClick={() => setLang('en')} 
                    className={`px-6 py-3 text-xs sm:text-sm font-black rounded-xl transition cursor-pointer flex items-center gap-2.5 shadow-lg active:scale-95 ${
                      lang === 'en' ? 'bg-indigo-600 text-white shadow-indigo-500/30' : 'bg-slate-900 text-slate-400 hover:text-white border border-slate-800'
                    }`}
                  >
                    <span>🇺🇸</span> English (ENG)
                  </button>
                  <button 
                    type="button"
                    onClick={() => setLang('pl')} 
                    className={`px-6 py-3 text-xs sm:text-sm font-black rounded-xl transition cursor-pointer flex items-center gap-2.5 shadow-lg active:scale-95 ${
                      lang === 'pl' ? 'bg-indigo-600 text-white shadow-indigo-500/30' : 'bg-slate-900 text-slate-400 hover:text-white border border-slate-800'
                    }`}
                  >
                    <span>🇵🇱</span> Polski (PL)
                  </button>
                </div>
              </div>

              {/* CARD 3: PROFILE AVATAR MANAGEMENT */}
              <div className="bg-slate-950/80 border border-slate-800 p-5 rounded-2xl space-y-4 shadow-inner">
                <div className="flex items-center gap-2 border-b border-slate-800 pb-3">
                  <span className="material-symbols-outlined text-indigo-400 text-xl">account_circle</span>
                  <strong className="text-sm font-black text-white uppercase tracking-wide">
                    {lang === 'pl' ? 'Zdjęcie Profilowe / Avatar' : 'Profile Photo & Avatar'}
                  </strong>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-5">
                  <div className="w-20 h-20 rounded-full border-2 border-indigo-500/50 bg-slate-900 overflow-hidden shrink-0 shadow-lg flex items-center justify-center">
                    {userProfile?.avatarUrl ? (
                      <img src={userProfile.avatarUrl} alt="Avatar Preview" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-indigo-600 to-slate-900 flex items-center justify-center text-xl font-black text-white uppercase tracking-wider select-none">
                        {(() => {
                          const nameStr = displayNameInput || userProfile?.name || userProfile?.displayName || user?.displayName || user?.email || "";
                          const parts = nameStr.trim().split(/\s+/);
                          if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
                          if (parts[0] && parts[0].length >= 2) return parts[0].substring(0, 2).toUpperCase();
                          return "GG";
                        })()}
                      </div>
                    )}
                  </div>

                  <div className="space-y-3 flex-1 w-full text-left">
                    <p className="text-xs text-slate-400 m-0 font-medium">
                      {lang === 'pl' 
                        ? "Wgraj własne zdjęcie profilowe lub wklej bezpośredni link do obrazu (JPEG, PNG):" 
                        : "Upload a custom profile picture or paste a direct image URL (JPEG, PNG):"}
                    </p>

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
                      <label className="bg-indigo-600 hover:bg-indigo-500 text-white font-extrabold text-xs px-4 py-2.5 rounded-xl transition cursor-pointer flex items-center justify-center gap-1.5 shadow-md active:scale-95 shrink-0">
                        <span className="material-symbols-outlined text-sm">upload</span>
                        <span>{lang === 'pl' ? 'Wgraj Zdjęcie z Urządzenia' : 'Upload Photo'}</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleAvatarFileUpload}
                          className="hidden"
                        />
                      </label>

                      {userProfile?.avatarUrl && (
                        <button
                          type="button"
                          onClick={() => saveAvatarUrl(null)}
                          className="bg-slate-900 hover:bg-rose-950/40 text-slate-400 hover:text-rose-400 border border-slate-800 hover:border-rose-500/40 px-3 py-2.5 rounded-xl text-xs font-bold transition cursor-pointer flex items-center justify-center gap-1 active:scale-95"
                        >
                          <span className="material-symbols-outlined text-sm text-rose-400">delete</span>
                          <span>{lang === 'pl' ? 'Użyj Inicjałów' : 'Reset to Initials'}</span>
                        </button>
                      )}
                    </div>

                    <div className="flex items-center gap-2 pt-1">
                      <input
                        type="text"
                        value={avatarUrlInput}
                        onChange={(e) => setAvatarUrlInput(e.target.value)}
                        placeholder={lang === 'pl' ? "LUB wklej link URL do zdjęcia..." : "OR paste image URL link..."}
                        className="flex-1 bg-slate-900 border border-slate-800 focus:border-indigo-500/60 text-slate-100 font-bold text-xs rounded-xl px-3.5 py-2.5 focus:outline-none shadow-inner"
                      />
                      <button
                        type="button"
                        disabled={!avatarUrlInput.trim()}
                        onClick={async () => {
                          if (avatarUrlInput.trim()) {
                            await saveAvatarUrl(avatarUrlInput.trim());
                            setAvatarUrlInput("");
                          }
                        }}
                        className="bg-slate-800 hover:bg-indigo-600 disabled:opacity-40 text-slate-200 hover:text-white font-extrabold text-xs px-4 py-2.5 rounded-xl transition cursor-pointer shrink-0"
                      >
                        {lang === 'pl' ? 'Zapisz URL' : 'Save URL'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* CARD 4: COACH PAIRING & INVITATION CODE */}
              <div className="bg-slate-950/80 border border-slate-800 p-5 rounded-2xl space-y-4 shadow-inner">
                <div className="flex items-center gap-2 border-b border-slate-800 pb-3">
                  <span className="material-symbols-outlined text-emerald-400 text-xl">key</span>
                  <strong className="text-sm font-black text-white uppercase tracking-wide">
                    {lang === 'pl' ? 'Twój Trener i Kod Połączenia' : 'Your Coach & Pairing Code'}
                  </strong>
                </div>

                {!userProfile?.trainerId ? (
                  <div className="bg-slate-900/90 border border-amber-500/30 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-inner">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-400 font-bold shrink-0">
                        <span className="material-symbols-outlined text-xl">person_off</span>
                      </div>
                      <div>
                        <span className="text-[10px] font-black text-amber-400 uppercase tracking-widest block">
                          {lang === 'pl' ? "STATUS POŁĄCZENIA Z TRENEREM" : "COACH PAIRING STATUS"}
                        </span>
                        <h4 className="text-sm font-black text-white m-0">
                          {lang === 'pl' ? "Brak przypisanego trenera" : "No coach assigned yet"}
                        </h4>
                      </div>
                    </div>

                    <span className="bg-amber-500/20 text-amber-300 border border-amber-500/40 px-2.5 py-1 rounded-lg text-xs font-extrabold flex items-center gap-1.5 shrink-0">
                      <span className="w-2 h-2 rounded-full bg-amber-400" />
                      <span>{lang === 'pl' ? "Niepołączono" : "Not Connected"}</span>
                    </span>
                  </div>
                ) : (
                  <div className="bg-slate-900/90 border border-emerald-500/30 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-inner">
                    <div className="flex items-center gap-3.5">
                      <div className="w-12 h-12 rounded-full border-2 border-emerald-500/50 bg-slate-950 overflow-hidden shrink-0 shadow-md flex items-center justify-center">
                        {(userProfile?.trainerAvatar || userProfile?.coachAvatarUrl || ((userProfile?.trainerId || userProfile?.trainerEmail || "").toLowerCase().includes("marek") ? "/marek_avatar.jpg" : "/wieslaw_avatar.jpg")) ? (
                          <img 
                            src={userProfile?.trainerAvatar || userProfile?.coachAvatarUrl || ((userProfile?.trainerId || userProfile?.trainerEmail || "").toLowerCase().includes("marek") ? "/marek_avatar.jpg" : "/wieslaw_avatar.jpg")} 
                            alt={userProfile?.trainerName || "Trainer Avatar"} 
                            className="w-full h-full object-cover" 
                          />
                        ) : (
                          <span className="material-symbols-outlined text-emerald-400 text-2xl">sports</span>
                        )}
                      </div>
                      <div>
                        <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest block">
                          {lang === 'pl' ? "AKTYWNY TRENER" : "ACTIVE COACH"}
                        </span>
                        <h4 className="text-base font-black text-white m-0 flex items-center gap-2">
                          <span>{(userProfile?.trainerId || userProfile?.trainerEmail || "").toLowerCase().includes("marek") ? "Trener Marek" : (userProfile?.trainerName || branding?.trainerName || "Trener Wiesław Piątek")}</span>
                        </h4>
                        {(userProfile?.trainerEmail || userProfile?.trainerId) && (
                          <span className="text-[11px] font-mono text-slate-400 block mt-0.5">
                            {userProfile?.trainerEmail || userProfile?.trainerId}
                          </span>
                        )}
                      </div>
                    </div>

                    <span className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 px-3 py-1 rounded-lg text-xs font-extrabold flex items-center gap-1.5 shrink-0 shadow-sm">
                      <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                      <span>{lang === 'pl' ? "Połączono" : "Connected"}</span>
                    </span>
                  </div>
                )}

                <form onSubmit={handleLinkCoachCode} className="space-y-3 pt-1">
                  <label className="block text-xs font-extrabold text-slate-300 uppercase tracking-wider">
                    {lang === 'pl' ? 'Wpisz lub Zmień Kod Trenera:' : 'Enter or Update Coach Code:'}
                  </label>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        value={inputCoachCode}
                        onChange={(e) => setInputCoachCode(e.target.value.toUpperCase())}
                        placeholder="GHOST-XXXX-XXXX"
                        className="w-full bg-slate-900 border border-slate-700 focus:border-emerald-500/60 text-emerald-300 font-mono font-black text-xs rounded-xl px-3.5 py-3 uppercase tracking-wider focus:outline-none shadow-inner"
                      />
                      <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-base pointer-events-none">
                        vpn_key
                      </span>
                    </div>

                    <button
                      type="submit"
                      disabled={!inputCoachCode.trim() || isPairingCoach}
                      className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 disabled:opacity-40 text-white font-extrabold text-xs px-5 py-3 rounded-xl transition cursor-pointer flex items-center justify-center gap-1.5 shadow-md active:scale-95 shrink-0"
                    >
                      <span className="material-symbols-outlined text-sm">link</span>
                      <span>{isPairingCoach ? (lang === 'pl' ? 'Łączenie...' : 'Connecting...') : (lang === 'pl' ? 'Połącz z Trenerem' : 'Connect with Coach')}</span>
                    </button>
                  </div>

                  {pairingNotice && (
                    <div className={`p-3 rounded-xl text-xs font-bold flex items-center gap-2 border mt-2 ${
                      pairingNotice.type === 'success' ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' : 'bg-rose-500/15 text-rose-300 border-rose-500/30'
                    }`}>
                      <span className="material-symbols-outlined text-sm">
                        {pairingNotice.type === 'success' ? 'check_circle' : 'error'}
                      </span>
                      <span>{pairingNotice.text}</span>
                    </div>
                  )}
                </form>
              </div>

              {/* CARD 5: SECURITY & PASSWORD CHANGE */}
              <div className="bg-slate-950/80 border border-slate-800 p-5 rounded-2xl space-y-4 shadow-inner">
                <div className="flex items-center gap-2 border-b border-slate-800 pb-3">
                  <span className="material-symbols-outlined text-emerald-400 text-xl">shield_lock</span>
                  <strong className="text-sm font-black text-white uppercase tracking-wide">
                    {lang === 'pl' ? 'Bezpieczeństwo i Zmiana Hasła' : 'Security & Password Change'}
                  </strong>
                </div>

                {/* Direct Password Form */}
                <form onSubmit={handleDirectPasswordChange} className="bg-slate-900/90 border border-slate-800 p-4 sm:p-5 rounded-xl space-y-4">
                  <div className="flex items-center justify-between border-b border-slate-800/60 pb-2.5">
                    <span className="text-xs font-black text-white flex items-center gap-1.5">
                      <span className="material-symbols-outlined text-base text-emerald-400">key</span>
                      <span>{lang === 'pl' ? "Ustaw Nowe Hasło" : "Set New Password"}</span>
                    </span>
                    <span className="text-[10px] text-slate-400 font-medium">
                      {lang === 'pl' ? "Wprowadź aktualne i nowe hasło (min. 6 znaków)" : "Enter current and new password (min. 6 chars)"}
                    </span>
                  </div>

                  <div className="space-y-3.5">
                    {/* 1. Current / Old Password */}
                    <div>
                      <label className="block text-[11px] font-bold text-slate-300 mb-1">
                        {lang === 'pl' ? "Stare Hasło (Dotychczasowe):" : "Current Password:"}
                      </label>
                      <div className="relative">
                        <input
                          type={showPasswords ? "text" : "password"}
                          value={oldPasswordInput}
                          onChange={(e) => setOldPasswordInput(e.target.value)}
                          placeholder={lang === 'pl' ? "Wpisz dotychczasowe hasło..." : "Type current password..."}
                          className="w-full bg-slate-950 border border-slate-700 focus:border-emerald-500 text-white font-mono text-xs rounded-xl px-3.5 py-2.5 pr-12 focus:outline-none shadow-inner"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords(!showPasswords)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-emerald-400 text-xs transition cursor-pointer flex items-center gap-1 bg-slate-850 hover:bg-slate-800 px-2 py-1 rounded-lg border border-slate-700/60 shadow-sm"
                          title={showPasswords ? "Hide Password" : "Show Password"}
                        >
                          <span className="material-symbols-outlined text-sm">
                            {showPasswords ? 'visibility_off' : 'visibility'}
                          </span>
                        </button>
                      </div>
                    </div>

                    {/* 2. New Password */}
                    <div>
                      <label className="block text-[11px] font-bold text-slate-300 mb-1">
                        {lang === 'pl' ? "Nowe Hasło:" : "New Password:"}
                      </label>
                      <div className="relative">
                        <input
                          type={showPasswords ? "text" : "password"}
                          value={newPasswordInput}
                          onChange={(e) => setNewPasswordInput(e.target.value)}
                          placeholder={lang === 'pl' ? "Wpisz nowe hasło (min. 6 znaków)..." : "Type new password..."}
                          className="w-full bg-slate-950 border border-slate-700 focus:border-emerald-500 text-white font-mono text-xs rounded-xl px-3.5 py-2.5 pr-12 focus:outline-none shadow-inner"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords(!showPasswords)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-emerald-400 text-xs transition cursor-pointer flex items-center gap-1 bg-slate-850 hover:bg-slate-800 px-2 py-1 rounded-lg border border-slate-700/60 shadow-sm"
                          title={showPasswords ? "Hide Password" : "Show Password"}
                        >
                          <span className="material-symbols-outlined text-sm">
                            {showPasswords ? 'visibility_off' : 'visibility'}
                          </span>
                        </button>
                      </div>
                    </div>

                    {/* 3. Confirm New Password */}
                    <div>
                      <label className="block text-[11px] font-bold text-slate-300 mb-1">
                        {lang === 'pl' ? "Powtórz Nowe Hasło:" : "Confirm New Password:"}
                      </label>
                      <div className="relative">
                        <input
                          type={showPasswords ? "text" : "password"}
                          value={confirmPasswordInput}
                          onChange={(e) => setConfirmPasswordInput(e.target.value)}
                          placeholder={lang === 'pl' ? "Powtórz nowe hasło..." : "Re-type new password..."}
                          className="w-full bg-slate-950 border border-slate-700 focus:border-emerald-500 text-white font-mono text-xs rounded-xl px-3.5 py-2.5 pr-12 focus:outline-none shadow-inner"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords(!showPasswords)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-emerald-400 text-xs transition cursor-pointer flex items-center gap-1 bg-slate-850 hover:bg-slate-800 px-2 py-1 rounded-lg border border-slate-700/60 shadow-sm"
                          title={showPasswords ? "Hide Password" : "Show Password"}
                        >
                          <span className="material-symbols-outlined text-sm">
                            {showPasswords ? 'visibility_off' : 'visibility'}
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isUpdatingPassword || !newPasswordInput.trim() || !confirmPasswordInput.trim()}
                    className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-slate-950 font-black text-xs py-3 rounded-xl transition cursor-pointer flex items-center justify-center gap-1.5 shadow-md active:scale-95 mt-1"
                  >
                    <span className="material-symbols-outlined text-sm">lock_reset</span>
                    <span>{isUpdatingPassword ? (lang === 'pl' ? 'Zapisywanie...' : 'Saving...') : (lang === 'pl' ? 'Zapisz Nowe Hasło' : 'Save New Password')}</span>
                  </button>
                </form>

                {/* Send Password Reset Link */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-2 border-t border-slate-900">
                  <div>
                    <h4 className="text-xs font-black text-white m-0">
                      {lang === 'pl' ? "Wysyłka Linku Resetu na E-mail" : "Send Password Reset Email"}
                    </h4>
                    <p className="text-[11px] text-slate-400 m-0 mt-0.5 font-medium">
                      {lang === 'pl' ? "Wyślij link z przyciskiem resetu z contact@gymghost.net." : "Send password reset link from contact@gymghost.net."}
                    </p>
                  </div>

                  <button
                    type="button"
                    disabled={isSendingPasswordReset}
                    onClick={handleSendPasswordReset}
                    className="bg-slate-850 hover:bg-slate-800 text-slate-200 hover:text-white border border-slate-750 font-extrabold text-xs px-4 py-2.5 rounded-xl transition cursor-pointer flex items-center justify-center gap-1.5 shrink-0"
                  >
                    <span className="material-symbols-outlined text-sm text-indigo-400">mail</span>
                    <span>{isSendingPasswordReset ? (lang === 'pl' ? 'Wysyłanie...' : 'Sending...') : (lang === 'pl' ? 'Wyślij Link Resetu' : 'Send Reset Link')}</span>
                  </button>
                </div>

                {passwordNotice && (
                  <div className={`p-3 rounded-xl text-xs font-bold flex items-center gap-2 border ${
                    passwordNotice.type === 'success' ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' : 'bg-rose-500/15 text-rose-300 border-rose-500/30'
                  }`}>
                    <span className="material-symbols-outlined text-sm">
                      {passwordNotice.type === 'success' ? 'check_circle' : 'error'}
                    </span>
                    <span>{passwordNotice.text}</span>
                  </div>
                )}
              </div>

              {/* CARD 6: ACCOUNT SIGN OUT (SEPARATE SECTION) */}
              <div className="bg-slate-950/80 border border-slate-800 p-5 rounded-2xl shadow-inner">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="material-symbols-outlined text-rose-400 text-2xl">logout</span>
                    <div>
                      <strong className="text-sm font-black text-white uppercase tracking-wide block">
                        {lang === 'pl' ? 'Wylogowanie z Konta' : 'Account Sign Out'}
                      </strong>
                      <p className="text-[11px] text-slate-400 m-0 font-medium">
                        {lang === 'pl' ? "Zakończ aktywną sesję aplikacji na tym urządzeniu." : "End active session on this device."}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={onSignOut}
                    className="bg-rose-950/40 hover:bg-rose-900/60 text-rose-300 border border-rose-500/30 font-black text-xs px-5 py-3 rounded-xl transition cursor-pointer flex items-center justify-center gap-1.5 shadow-sm active:scale-95 shrink-0"
                  >
                    <span className="material-symbols-outlined text-sm">logout</span>
                    <span>{lang === 'pl' ? 'Wyloguj się' : 'Sign Out'}</span>
                  </button>
                </div>
              </div>

            </div>
          </div>
        )}

      </main>

    

</div>
  );
}
